import L, { type Layer, type LeafletKeyboardEvent, type Marker } from 'leaflet'
import { leafletLayer } from 'protomaps-leaflet'
import mapData from '../map-data.lock.json'
import { createLoadingToy } from './loading-toy.ts'
import { NYC_BOUNDS } from '../functions/lib/validation.ts'
import { bikeShareSnapshot } from './bike-share/client.ts'
import { BusClient } from './bus/client.ts'
import { FerryClient } from './ferry/client.ts'
import { MultimodalClient } from './multimodal/client.ts'
import type { ScheduledMode } from './multimodal/data.ts'
import { decodePlannerDraft, type CurrentPlannerDraft } from './planner-draft.ts'
import { ClientIncompatibleError, isClientIncompatible, plannerApiPayload, plannerApiUrl } from './planner-api.ts'
import { addServiceDays, discoveryDeparture, newYorkDateTime, timeSeconds, upcomingServiceDates, type PlannerDateTime } from './planner-time.ts'
import {
  clientDiagnosticState,
  diagnosticSchemaVersion,
  DiagnosticSubmissionError,
  submitAutomaticDiagnostic,
  submitDiagnostic,
  type DiagnosticIssueCode,
  type DiagnosticPhase,
  type DiagnosticReceipt,
  type DiagnosticReport,
} from './diagnostics.ts'
import type { LocationPoint, Venue } from './planning.ts'
import { roadGeometry, roadMatrix, type RoadGeometry, type RoadProfile } from './road/client.ts'
import { initializePwaControls } from './pwa-controls.ts'
import { createResultsSheet } from './results-sheet.ts'
import { modeIcon, modeLabel, routeSummaryHtml, walkingSummaryHtml } from './route-summary.ts'
import { routeTransitions } from './route-transitions.ts'
import { loadSavedPlaces, persistSavedPlaces, type SavedPlace } from './saved-places.ts'
import { scheduledGeometry } from './scheduled-geometry.ts'
import { createSearchRouting, routingBackend, scheduledModesFor, type RoutingBackend } from './search-routing.ts'
import { searchPlaces, type PlannedVenue, type SearchPhaseId, type SearchRequest, type SearchResult, type SearchTime } from './search.ts'
import { TransitClient } from './transit/client.ts'
import type { RankingPreference } from './transit/candidates.ts'
import { searchVenues } from './venue-search.ts'
import {
  isBikeSharePerson,
  isScheduledPerson,
  scheduledAlternatives,
  type EndpointJourney,
  type RoadMode,
  type RoutedParticipant,
  type TravelChoice,
  type TravelMode,
  type TravelPermissionSet,
} from './transit/modes.ts'

interface OriginSelectionSource {
  type: 'device'
  approximate: boolean
}

interface OriginState {
  id: number
  point: LocationPoint | null
  choice: TravelChoice
  generation: number
  selectionSource?: OriginSelectionSource
  layer?: Layer
}

type TimeIntent = 'depart-at' | 'arrive-by'

interface RoutingDiagnostic {
  person: string
  choice: TravelChoice
  reachableEndpoints: number
  totalEndpoints: number
}

type SearchDiagnostic =
  | { state: 'searching' }
  | { state: 'empty'; code: 'no_shared_transit_area' | 'no_candidate_area'; routing: RoutingDiagnostic[] }
  | {
    state: 'partial'
    code: 'areas_only'
    reason: 'venue_unavailable'
    routing: RoutingDiagnostic[]
    areas: Array<Pick<PlannedVenue, 'lat' | 'lon' | 'minutes' | 'range'>>
    message?: string
  }
  | {
    state: 'empty' | 'results'
    code: 'no_places_fit' | 'places_found'
    venueCandidates: number
    routing: RoutingDiagnostic[] | null
    results: Array<Pick<PlannedVenue, 'id' | 'name' | 'type' | 'minutes' | 'range'>>
  }
  | { state: 'error'; code: 'search_failed'; message: string }

type SearchPhaseState = 'pending' | 'active' | 'complete' | 'failed'

interface SearchPhase {
  id: SearchPhaseId
  activeLabel: string
  completeLabel: string
  state: SearchPhaseState
  startedAt?: number
  durationMs?: number
}

interface SearchProgress {
  search: number
  phases: SearchPhase[]
}

interface AreaRecovery {
  request: SearchRequest
  result: Extract<SearchResult, { kind: 'areas' }>
  tripContext: DiagnosticReport['tripContext']
  selectedIndex: number | null
  pending: boolean
}

interface DiagnosticSnapshot {
  sequence: number
  report: DiagnosticReport
  tripContext?: DiagnosticReport['tripContext']
}

const travelModeOptions: Array<{ value: TravelMode; label: string; checked: boolean }> = [
  { value: 'walk', label: 'Walking only', checked: true },
  { value: 'subway', label: 'Subway', checked: true },
  { value: 'bus', label: 'Bus', checked: false },
  { value: 'ferry', label: 'Ferry', checked: false },
  { value: 'taxi', label: 'Taxi / ride-share', checked: false },
  { value: 'bike', label: 'Your bicycle', checked: false },
  { value: 'bike-share', label: 'Bike share', checked: false },
]

const minimumOrigins = 2
const maximumOrigins = 6
const rankingPreference: RankingPreference = 'balanced'
const compactResults = matchMedia('(max-width: 820px)')
const resultsPreviewSession = __RESULTS_PREVIEW__ && new URLSearchParams(location.search).get('fixture') === 'results'
let fixtureRoutePreviews: Record<string, Array<{
  personIndex: number; walking: boolean; coordinates: Array<[number, number]>
}>> | null = null
let nextOriginId = 1
const origins: OriginState[] = []
let locationRequest: { origin: OriginState; generation: number } | null = null
let venueLayers: Layer[] = []
let previewLayers: Layer[] = []
const previewPeople = new WeakMap<L.Polyline, number>()
let previewController: AbortController | null = null
let previewGeneration = 0
const previewCache = new Map<string, RoadGeometry>()
let retryRoutePreview: (() => Promise<void>) | null = null
let refreshRouteRecovery: (() => void) | null = null
let activeSearch = 0
let searchController: AbortController | null = null
let hasResults = false
let searchDiagnostic: SearchDiagnostic | null = null
let areaRecovery: AreaRecovery | null = null
let lastBikeShareUpdatedAt: number | null = null
let searchProgress: SearchProgress | null = null
let searchedDeparture: SearchTime | null = null
let departureEdited = false
let leaveNow = true
let lastSearchPhases: Array<Pick<SearchPhase, 'id' | 'state' | 'durationMs'>> = []
let diagnosticSequence = 0
let latestDiagnostic: DiagnosticSnapshot | null = null
let latestDiagnosticReceipt: { sequence: number; receipt: DiagnosticReceipt } | null = null
let dialogDiagnostic: DiagnosticSnapshot | null = null
let dialogReport: DiagnosticReport | null = null
let fallbackReport: DiagnosticReport | null = null
let acceptedReportId: string | null = null
let dialogGeneration = 0
let synchronizeVenueDisclosure: (() => void) | null = null
let savedPlacesStorage: Storage | undefined
try { savedPlacesStorage = window.localStorage } catch { /* Local storage may be disabled. */ }
const initialSavedPlaces = savedPlacesStorage
  ? loadSavedPlaces(savedPlacesStorage) : { places: [], writable: false }
let savedPlaces = initialSavedPlaces.places
let deletedPlace: { place: SavedPlace; index: number } | null = null
const savedPlacesWritable = initialSavedPlaces.writable

const transit = new TransitClient(navigator.onLine)
let bus: BusClient | null = null
let ferry: FerryClient | null = null
let multimodal: MultimodalClient | null = null
const map = L.map('map', { zoomControl: false }).setView([40.73, -73.98], 11)
L.control.zoom({ position: 'bottomright' }).addTo(map)
// Credit belongs to the shell, including when offline and no tile layer exists.
map.attributionControl.setPrefix(false)
map.attributionControl.addAttribution('© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://www.openstreetmap.org/fixthemap">Fix map</a>')
const mapAttribution = requiredElement<HTMLElement>('#map .leaflet-control-attribution')
// Keep Leaflet's live credit above overlays and outside the inert map subtree.
requiredElement<HTMLElement>('.results-panel').appendChild(mapAttribution)
if (navigator.onLine) {
  const basemap = mapData.basemaps.at(-1)!
  const options = {
    url: `/api/tiles/${basemap.sha256}/{z}/{x}/{y}.mvt`,
    lang: 'en', maxDataZoom: basemap.maxZoom, maxZoom: 19,
    attribution: '',
  }
  // protomaps-leaflet 5.1.0: share data caches, never style-dependent labelers or
  // canvases. Recheck this public runtime property when upgrading the renderer.
  // Retired async paints can only touch detached canvases; the camera and all
  // overlays belong to the persistent Leaflet map, not this replaceable layer.
  type BasemapLayer = L.GridLayer & { views: Map<string, import('protomaps-leaflet').View> }
  const createBasemap = () => leafletLayer({ ...options, flavor: document.documentElement.dataset.theme ?? 'light' }) as unknown as BasemapLayer
  let layer = createBasemap().addTo(map)
  window.addEventListener('themechange', () => {
    const next = createBasemap()
    next.views = layer.views
    layer.remove()
    layer = next.addTo(map)
  })
}

window.addEventListener('themechange', () => {
  for (const layer of previewLayers) {
    if (layer instanceof L.Polyline) layer.setStyle({ color: routeColor(previewPeople.get(layer)!) })
  }
  for (const layer of venueLayers) {
    if (layer instanceof L.Circle) layer.setStyle({ color: routeColor(0), fillColor: routeColor(0) })
  }
})

const originsContainer = requiredElement<HTMLDivElement>('#origins')
const addPersonButton = requiredElement<HTMLButtonElement>('#add-person')
const status = requiredElement<HTMLParagraphElement>('#status')
const findButton = requiredElement<HTMLButtonElement>('#find-places')
const searchProgressRegion = requiredElement<HTMLDivElement>('#search-progress-region')
const recommendationList = requiredElement<HTMLDivElement>('#recommendations')
const routingMode = requiredElement<HTMLParagraphElement>('#routing-mode')
const scheduleNote = requiredElement<HTMLParagraphElement>('#schedule-note')
const resultsTitle = requiredElement<HTMLHeadingElement>('#results-title')
const searchedTime = requiredElement<HTMLParagraphElement>('#searched-time')
const departureDate = requiredElement<HTMLSelectElement>('#departure-date')
const departureTime = requiredElement<HTMLInputElement>('#departure-time')
const timeIntentInputs = [...document.querySelectorAll<HTMLInputElement>('input[name="time-intent"]')]
const reportProblemButton = requiredElement<HTMLButtonElement>('#report-problem')
const reportSummary = requiredElement<HTMLSpanElement>('#report-summary')
const reportDialog = requiredElement<HTMLDialogElement>('#report-dialog')
const reportIssue = requiredElement<HTMLSelectElement>('#report-issue')
const reportTripContext = requiredElement<HTMLInputElement>('#report-trip-context')
const reportPreview = requiredElement<HTMLElement>('#report-preview')
const sendReportButton = requiredElement<HTMLButtonElement>('#send-report')
const copyReportButton = requiredElement<HTMLButtonElement>('#copy-report')
const reportStatus = requiredElement<HTMLParagraphElement>('#report-status')
const settingsButton = requiredElement<HTMLButtonElement>('#settings-button')
const settingsPanel = requiredElement<HTMLDialogElement>('#settings-panel')
const closeSettingsButton = requiredElement<HTMLButtonElement>('#close-settings')
const savedPlacesSetting = requiredElement<HTMLElement>('#saved-places-setting')
const savedPlacesList = requiredElement<HTMLDivElement>('#saved-places-list')
const appSettings = requiredElement<HTMLElement>('#app-settings')
const installButton = requiredElement<HTMLButtonElement>('#install-app')
const installDialog = requiredElement<HTMLDialogElement>('#install-dialog')
const updateDialog = requiredElement<HTMLDialogElement>('#update-dialog')
requiredElement<HTMLElement>('#input-screen').appendChild(routingMode)

type PlannerScreen = 'input' | 'loading' | 'results'
let screen: PlannerScreen = 'input'
let searchOpen = true
let clientIncompatible = false
let completedSearch: number | null = null
let pendingMapBounds: L.LatLngBoundsExpression | null = null
history.scrollRestoration = 'manual'
history.replaceState({ ...history.state, plannerScreen: 'input' }, '')

function validCompletion(): boolean {
  return completedSearch !== null && completedSearch === activeSearch
}

const recommendationsPanel = requiredElement<HTMLElement>('.recommendations')
const resultsPanel = requiredElement<HTMLElement>('.results-panel')
const resultsSheetElement = requiredElement<HTMLElement>('.results-sheet')
const resultsError = requiredElement<HTMLElement>('#results-error')
const settingsWrapper = requiredElement<HTMLElement>('.settings')
const loadingToy = createLoadingToy(requiredElement<HTMLButtonElement>('#meet-toy'))
let pendingSelection: { point: L.LatLngExpression; zoom: number } | null = null
const resultsSheet = createResultsSheet({
  panel: resultsPanel,
  sheet: resultsSheetElement,
  content: recommendationsPanel,
  handle: requiredElement<HTMLButtonElement>('#sheet-handle'),
  restore: requiredElement<HTMLButtonElement>('#show-places'),
  map: map.getContainer(),
  availableHeight: () => requiredElement<HTMLElement>('#results-notices').getBoundingClientRect().top
    - resultsPanel.getBoundingClientRect().top,
  mapIsUsable: () => {
    const { top, bottom, left, right } = mapInsets()
    return map.getContainer().clientHeight - top - bottom >= 44
      && map.getContainer().clientWidth - left - right >= 44
  },
  compact: compactResults,
  onChange: () => requestAnimationFrame(refreshResultsMap),
})

// The map keeps its dimensions while the sheet moves. Camera operations use only
// the unobscured rectangle, and pure snaps leave an existing camera untouched.
function mapInsets(): { top: number; bottom: number; left: number; right: number } {
  const bounds = map.getContainer().getBoundingClientRect()
  const tools = requiredElement<HTMLElement>('.results-tools').getBoundingClientRect()
  const sheet = resultsSheetElement.getBoundingClientRect()
  const planner = requiredElement<HTMLElement>('.planner').getBoundingClientRect()
  const notices = requiredElement<HTMLElement>('#results-notices').getBoundingClientRect()
  const attribution = mapAttribution.getBoundingClientRect()
  const raisedAttribution = screen === 'results' && compactResults.matches && !searchOpen
    && !resultsSheetElement.hidden && attribution.bottom <= sheet.top
  return {
    top: Math.max(16, tools.bottom - bounds.top + 12,
      compactResults.matches && searchOpen ? planner.bottom - bounds.top + 12 : 0),
    left: !compactResults.matches ? Math.max(16, searchOpen ? planner.right - bounds.left + 16
      : screen === 'results' && !resultsSheetElement.hidden ? sheet.right - bounds.left + 16 : 16) : 16,
    right: raisedAttribution ? Math.max(16, bounds.right - attribution.left + 12) : 16,
    bottom: Math.max(bounds.bottom - (raisedAttribution ? notices.bottom : attribution.top) + 28,
      notices.height ? bounds.bottom - notices.top + 28 : 0,
      screen === 'results' && compactResults.matches && !resultsSheetElement.hidden
        ? Math.max(0, bounds.bottom - sheet.top) + 16 : 0),
  }
}

function refreshResultsMap(): void {
  if (!map.getContainer().clientWidth) return
  const shell = requiredElement<HTMLElement>('main')
  const attributionHeight = mapAttribution.getBoundingClientRect().height
  const noticeHeight = requiredElement<HTMLElement>('#results-notices').getBoundingClientRect().height
  const toolsBottom = requiredElement<HTMLElement>('.results-tools').getBoundingClientRect().bottom
  shell.style.setProperty('--attribution-height', `calc(${attributionHeight}px + env(safe-area-inset-bottom) + var(--attribution-gap))`)
  shell.style.setProperty('--bottom-clearance', `calc(${attributionHeight + noticeHeight}px + env(safe-area-inset-bottom) + var(--attribution-gap))`)
  shell.style.setProperty('--overlay-top', `${toolsBottom + 10}px`)
  const insets = mapInsets()
  resultsPanel.style.setProperty('--results-tools-clearance', `${toolsBottom + 10}px`)
  const visibleHeight = map.getContainer().clientHeight - insets.top - insets.bottom
  const visibleWidth = map.getContainer().clientWidth - insets.left - insets.right
  const mapUnavailable = visibleHeight < 44 || visibleWidth < 44
    || (screen === 'results' && !resultsSheetElement.hidden && compactResults.matches && resultsSheet.getState() === 'full')
  const spaceLimited = compactResults.matches && visibleHeight < 100
  if ((mapUnavailable || spaceLimited) && map.getContainer().contains(document.activeElement)) {
    const target = searchOpen ? '#page-title' : searchDiagnostic?.state === 'empty' ? '#edit-empty-search' : screen === 'results'
      ? resultsSheetElement.hidden ? '#show-places' : '#sheet-handle' : '#edit-search'
    requiredElement<HTMLElement>(target).focus({ preventScroll: true })
  }
  map.getContainer().inert = mapUnavailable
  resultsPanel.classList.toggle('map-space-limited', spaceLimited)
  map.invalidateSize({ pan: false })
  if (screen !== 'results') return
  // Explicit selections made under a full drawer wait for usable map space.
  // Ordinary snaps never create camera work; edits clear pending result work.
  if (map.getContainer().inert) return
  if (pendingSelection) {
    const { point, zoom } = pendingSelection
    pendingSelection = null
    // Put a new selection at the center of the visible map, not under the sheet.
    const projected = map.project(point, zoom)
    projected.x += (insets.right - insets.left) / 2
    projected.y += (insets.bottom - insets.top) / 2
    map.stop()
    map.setView(map.unproject(projected, zoom), zoom, { animate: false })
  } else if (pendingMapBounds) {
    map.stop()
    map.fitBounds(pendingMapBounds, {
      paddingTopLeft: [insets.left, insets.top], paddingBottomRight: [insets.right, insets.bottom], animate: false,
    })
    pendingMapBounds = null
  }
  revealFocusedMapMarker()
}

function positionSelectedPlace(point: L.LatLngExpression, zoom: number): void {
  pendingMapBounds = null
  pendingSelection = { point, zoom }
  refreshResultsMap()
}

const resultsMapResize = new ResizeObserver(() => {
  resultsSheet.refresh()
  refreshResultsMap()
  const focused = document.activeElement
  if ((window.visualViewport?.scale ?? 1) === 1
    && focused instanceof HTMLElement && focused.closest('.planner-content')) {
    focused.scrollIntoView({ block: 'nearest' })
  }
})
resultsMapResize.observe(map.getContainer())
resultsMapResize.observe(requiredElement<HTMLElement>('.results-tools'))
resultsMapResize.observe(requiredElement<HTMLElement>('.planner'))
resultsMapResize.observe(requiredElement<HTMLElement>('.planner-content'))
resultsMapResize.observe(requiredElement<HTMLElement>('#results-notices'))
resultsMapResize.observe(mapAttribution, { box: 'border-box' })
map.getContainer().addEventListener('focusin', () => requestAnimationFrame(revealFocusedMapMarker))

function revealFocusedMapMarker(): void {
  const active = document.activeElement
  if (!(active instanceof HTMLElement)
    || !active.classList.contains('leaflet-marker-icon') || map.getContainer().inert) return
  // Stop Leaflet's focus/inertia pan before measuring; it could overwrite our correction.
  map.stop()
  const marker = active.getBoundingClientRect()
  const bounds = map.getContainer().getBoundingClientRect()
  const { top, bottom, left, right } = mapInsets()
  const dx = marker.left < bounds.left + left ? marker.left - bounds.left - left
    : Math.max(0, marker.right - bounds.right + right)
  const dy = marker.top < bounds.top + top ? marker.top - bounds.top - top
    : Math.max(0, marker.bottom - bounds.bottom + bottom)
  if (dx || dy) map.panBy([dx, dy], { animate: false })
}

function showScreen(next: PlannerScreen): void {
  closeSavedPlacePickers()
  closeSettings(false)
  resultsSheet.cancelDrag()
  const tools = requiredElement<HTMLElement>('.results-tools')
  if (settingsWrapper.parentElement !== tools) tools.appendChild(settingsWrapper)
  const notice = requiredElement<HTMLElement>('#client-incompatible-notice')
  const noticeContainer = requiredElement<HTMLElement>('#results-notices')
  noticeContainer.insertBefore(notice, noticeContainer.firstChild)
  screen = next
  document.body.dataset.screen = next
  document.body.dataset.searchOpen = String(searchOpen)
  requiredElement<HTMLElement>('.planner').hidden = !searchOpen
  requiredElement<HTMLElement>('#input-screen').hidden = next !== 'input'
  requiredElement<HTMLElement>('#input-actions').hidden = next !== 'input'
  requiredElement<HTMLElement>('#loading-screen').hidden = next !== 'loading'
  requiredElement<HTMLElement>('#loading-play').hidden = next !== 'loading'
  loadingToy.setActive(next === 'loading')
  requiredElement<HTMLElement>('#results-screen').hidden = false
  if (next === 'input') findButton.parentElement!.insertBefore(status, findButton)
  else requiredElement<HTMLElement>(next === 'results' ? '#results-notices' : '.planner-content').appendChild(status)
  const offlineContainer = requiredElement<HTMLElement>(next === 'input' ? '#input-actions' : '#results-notices')
  offlineContainer.insertBefore(requiredElement<HTMLElement>('#offline-notice'), offlineContainer.firstChild)
  if (next === 'results' && searchDiagnostic?.state === 'empty') {
    noticeContainer.insertBefore(status, requiredElement<HTMLElement>('#edit-empty-search'))
  }
  resultsSheet.setActive(next === 'results' && !searchOpen && searchDiagnostic?.state !== 'empty')
  map.closePopup()
  for (const layer of [...venueLayers, ...previewLayers]) {
    if (layer instanceof L.Marker) {
      const element = layer.getElement()
      if (element) {
        element.inert = next !== 'results'
        element.style.pointerEvents = next === 'results' ? '' : 'none'
        element.tabIndex = next === 'results' ? 0 : -1
      }
    } else if (layer instanceof L.Path) {
      const element = layer.getElement()
      if (element instanceof SVGElement) element.style.pointerEvents = next === 'results' ? '' : 'none'
    }
  }
  for (const selector of ['#retry-search', '#edit-empty-search', '#area-search-actions']) requiredElement<HTMLElement>(selector).inert = next !== 'results'
  updateSearchToggle()
  status.classList.toggle('visually-hidden', validCompletion() && searchDiagnostic?.state === 'results')
  refreshResultsNoticeFocus()
  routingMode.hidden = next !== 'results' && hasResults
  routingMode.classList.toggle('visually-hidden', next === 'results')
  if (next === 'results') resultsTitle.parentNode?.insertBefore(routingMode, resultsTitle)
  else requiredElement<HTMLElement>('#input-screen').appendChild(routingMode)
  const target = next === 'input'
    ? requiredElement<HTMLElement>('#page-title')
    : requiredElement<HTMLElement>(`#${next}-screen h2`)
  if (searchOpen || next === 'results') target?.focus({ preventScroll: true })
  requiredElement<HTMLElement>('.planner-content').scrollTop = 0
  window.scrollTo(0, 0)
  requestAnimationFrame(refreshResultsMap)
}

function refreshResultsNoticeFocus(): void {
  requiredElement<HTMLElement>('#results-notices').tabIndex = searchDiagnostic?.state === 'results'
    && requiredElement<HTMLElement>('#client-incompatible-notice').hidden && resultsError.hidden
    && requiredElement<HTMLElement>('#offline-notice').hidden
    && requiredElement<HTMLElement>('#route-recovery').hidden ? -1 : 0
}

function updateSearchToggle(): void {
  const toggle = requiredElement<HTMLButtonElement>('#edit-search')
  requiredElement<HTMLElement>('#results-settings').hidden = !validCompletion()
  const type = document.querySelector<HTMLInputElement>('input[name="venue-type"]:checked')?.value ?? 'coffee'
  const when = document.querySelector<HTMLInputElement>('input[name="time-intent"]:checked')?.value
  toggle.textContent = clientIncompatible && document.body.classList.contains('short-viewport') ? 'Search'
    : !validCompletion() ? 'Find a meeting place'
    : `${origins.length} people · ${type === 'coffee' ? 'Coffee' : type === 'brunch' ? 'Brunch' : 'Drinks'} · ${when === 'arrive-by' ? 'Arrive by' : when === 'depart-at' ? 'Leave at' : 'Leave now'}`
  toggle.setAttribute('aria-label', validCompletion() ? 'Edit search' : 'Open search')
  toggle.setAttribute('aria-expanded', String(searchOpen))
  toggle.setAttribute('aria-controls', 'input-screen')
  toggle.hidden = searchOpen
  refreshRouteRecovery?.()
}

requiredElement<HTMLButtonElement>('#retry-routes').addEventListener('click', () => {
  if (screen === 'results' && !searchOpen && !clientIncompatible) void retryRoutePreview?.()
})

function returnToEditing(): void {
  if (screen === 'loading') invalidateSearch()
  pendingMapBounds = null
  pendingSelection = null
  if (!validCompletion()) status.textContent = ''
  searchOpen = true
  showScreen('input')
}

window.addEventListener('popstate', () => {
  if (history.state?.plannerScreen === 'results' && validCompletion() && history.state.submittedSearch === completedSearch) {
    searchOpen = false
    showScreen('results')
  } else {
    returnToEditing()
  }
})
requiredElement<HTMLButtonElement>('#cancel-search').addEventListener('click', () => {
  // Invalidate synchronously: a response can settle before popstate is delivered.
  returnToEditing()
  history.back()
})
requiredElement<HTMLButtonElement>('#edit-search').addEventListener('click', () => {
  if (screen === 'loading') return
  if (searchOpen) closeSearch()
  else returnToEditing()
})
requiredElement<HTMLButtonElement>('#edit-empty-search').addEventListener('click', returnToEditing)
function closeSearch(): void {
  if (screen === 'loading') return
  searchOpen = false
  showScreen(validCompletion() ? 'results' : 'input')
  if (!validCompletion()) requiredElement<HTMLButtonElement>('#edit-search').focus({ preventScroll: true })
}
requiredElement<HTMLButtonElement>('#close-search').addEventListener('click', closeSearch)
requiredElement<HTMLButtonElement>('#retry-search').addEventListener('click', () => {
  if (screen === 'results' && !searchOpen) void findPlaces()
})
requiredElement<HTMLButtonElement>('#search-area').addEventListener('click', () => {
  if (screen === 'results' && !searchOpen && areaRecovery) void findPlaces(areaRecovery)
})
requiredElement<HTMLButtonElement>('#new-search').addEventListener('click', () => {
  closeSavedPlacePickers()
  invalidateSearch()
  locationRequest = null
  origins.forEach((origin) => { origin.generation += 1; origin.layer?.remove() })
  origins.length = 0
  originsContainer.replaceChildren()
  for (let index = 0; index < minimumOrigins; index += 1) addOrigin(false)
  setCheckedControl('venue-type', 'coffee')
  setTimeIntent('leave-now')
  setDepartureSelection(newYorkDateTime())
  departureEdited = false
  refreshModeControls()
  status.textContent = ''
  history.replaceState({ ...history.state, plannerScreen: 'input', submittedSearch: null }, '')
  returnToEditing()
})
let lastResultsFocus: HTMLElement | null = null
document.addEventListener('focusin', (event) => {
  lastResultsFocus = event.target instanceof HTMLElement && event.target.closest('.results-panel')
    ? event.target : null
})
compactResults.addEventListener('change', () => {
  if (screen !== 'results') return
  // Browsers can blur a CSS-hidden control before dispatching the media change.
  const focused = document.activeElement === document.body ? lastResultsFocus : document.activeElement
  const closingFocusedPopup = compactResults.matches && focused instanceof HTMLElement && focused.closest('.leaflet-popup')
  if (compactResults.matches) map.closePopup()
  if (focused instanceof HTMLElement && focused.isConnected
    && (!focused.checkVisibility() || focused.closest('[inert]') || closingFocusedPopup)) {
    const target = compactResults.matches
      ? requiredElement<HTMLButtonElement>(resultsSheet.getState() === 'collapsed' ? '#show-places' : '#sheet-handle')
      : recommendationsPanel
    target.focus({ preventScroll: true })
  }
})

const initialDeparture = newYorkDateTime()
for (let index = 0; index < minimumOrigins; index += 1) addOrigin(false)
departureTime.value = initialDeparture.time
updateTimeControlLabels()
trackVisualViewport()
initializeSettings()
initializeProblemReporting()
showScreen('input')
renderSavedPlaceSettings()
refreshSettingsAvailability()
initializePwaControls({
  installButton,
  installDialog,
  closeSettings: () => closeSettings(false),
  refreshSettingsAvailability,
})
const refreshConnectivity = () => {
  requiredElement<HTMLElement>('#offline-notice').hidden = navigator.onLine
  refreshModeControls()
  refreshRouteRecovery?.()
  refreshResultsNoticeFocus()
}
refreshConnectivity()
window.addEventListener('online', refreshConnectivity)
window.addEventListener('offline', refreshConnectivity)
requiredElement<HTMLButtonElement>('#undo-delete-place').addEventListener('click', () => {
  if (!deletedPlace) return
  const { place, index } = deletedPlace
  const next = [...savedPlaces]
  next.splice(index, 0, place)
  if (updateSavedPlaces(next, requiredElement<HTMLElement>('#saved-place-undo-error'))) {
    savedPlacesList.children[index]?.querySelector<HTMLButtonElement>('button')?.focus()
  }
})
window.addEventListener('planner-api-incompatible', () => {
  if (clientIncompatible) return
  clientIncompatible = true
  searchController?.abort(new ClientIncompatibleError())
  locationRequest = null
  origins.forEach((origin) => {
    origin.generation += 1
    const form = requiredElement<HTMLFormElement>(`[data-origin-id="${origin.id}"]`)
    requiredChild<HTMLDivElement>(form, '.search-results').querySelector('.loading')?.remove()
  })
  refreshSavedPlaceControls()
  previewGeneration += 1
  previewController?.abort()
  refreshRouteRecovery?.()
  document.querySelectorAll<HTMLElement>('.route-preview-status').forEach((message) => {
    message.classList.remove('visually-hidden')
    message.textContent = 'Route preview unavailable for this app version.'
  })
  if (searchDiagnostic?.state === 'searching') {
    activeSearch += 1
    searchDiagnostic = null
    clearSearchProgress()
    status.classList.remove('status-searching')
  }
  if (screen === 'loading') {
    const next = areaRecovery?.pending ? 'results' : 'input'
    if (areaRecovery?.pending) {
      areaRecovery.pending = false
      completedSearch = activeSearch
      status.textContent = 'Place search stopped. Your candidate areas are still available.'
    }
    history.replaceState({ ...history.state, plannerScreen: next, submittedSearch: completedSearch }, '')
    searchOpen = next !== 'results'
    showScreen(next)
  }
  requiredElement<HTMLElement>('#client-incompatible-notice').hidden = false
  requiredElement<HTMLElement>('#results-notices').tabIndex = 0
  updateSearchToggle()
  refreshModeControls()
})
void initializePlanner()

addPersonButton.addEventListener('click', () => addOrigin(true))
document.querySelectorAll<HTMLInputElement>('input[name="venue-type"]').forEach((input) => {
  input.addEventListener('change', invalidateSearch)
})
departureDate.addEventListener('change', departureChanged)
departureTime.addEventListener('change', departureChanged)
timeIntentInputs.forEach((input) => input.addEventListener('change', () => {
  const wasLeaveNow = leaveNow
  leaveNow = input.value === 'leave-now'
  if (leaveNow || wasLeaveNow) {
    setDepartureSelection(input.value === 'arrive-by' ? defaultArrivalTime() : newYorkDateTime())
  }
  departureEdited = true
  updateTimeControlLabels()
  invalidateSearch()
  if (!leaveNow) departureTime.scrollIntoView({ block: 'nearest' })
}))
findButton.addEventListener('click', () => void findPlaces())

function initializeProblemReporting(): void {
  reportProblemButton.addEventListener('click', () => {
    closeSettings(false)
    dialogGeneration += 1
    dialogDiagnostic = latestDiagnostic ?? {
      sequence: diagnosticSequence,
      report: diagnosticReportFor('other_problem', 'app', 'other', null),
      tripContext: currentTripContext(),
    }
    const recentReceipt = latestDiagnosticReceipt?.sequence === dialogDiagnostic.sequence
      ? latestDiagnosticReceipt.receipt : null
    reportStatus.textContent = recentReceipt
      ? `Recent report ${recentReceipt.id} was received at ${formatReportTime(recentReceipt.receivedAt)}.` : ''
    acceptedReportId = null
    copyReportButton.hidden = true
    copyReportButton.textContent = 'Copy report details'
    sendReportButton.hidden = false
    requiredElement<HTMLElement>('#report-done').hidden = true
    requiredElement<HTMLElement>('#report-support').hidden = true
    sendReportButton.disabled = false
    reportIssue.disabled = false
    reportIssue.value = hasResults ? 'incorrect_results' : 'other_problem'
    reportTripContext.disabled = false
    reportTripContext.checked = false
    refreshDialogReport(false)
    reportDialog.showModal()
  })
  reportIssue.addEventListener('change', () => refreshDialogReport(true))
  reportTripContext.addEventListener('change', () => refreshDialogReport(true))
  sendReportButton.addEventListener('click', () => void sendManualReport())
  copyReportButton.addEventListener('click', () => void copyManualReport())
  requiredElement<HTMLButtonElement>('#report-done').addEventListener('click', () => reportDialog.close())
  reportDialog.addEventListener('close', () => {
    dialogGeneration += 1
    dialogDiagnostic = null
    dialogReport = null
    fallbackReport = null
    settingsButton.focus()
  })
}

function refreshDialogReport(clearStatus: boolean): void {
  if (!dialogDiagnostic) return
  const issueCode: DiagnosticIssueCode = reportIssue.value === 'incorrect_results'
    ? 'incorrect_results' : 'other_problem'
  const incidentCodes: DiagnosticIssueCode[] = dialogDiagnostic.report.issueCode === 'other_problem'
    ? [] : dialogDiagnostic.report.operation.errorCodes
  dialogReport = {
    ...dialogDiagnostic.report,
    kind: 'manual',
    issueCode,
    operation: {
      ...dialogDiagnostic.report.operation,
      outcome: issueCode === 'incorrect_results' ? 'incorrect' : 'other',
      errorCodes: [...new Set([issueCode, ...incidentCodes])],
    },
    ...(reportTripContext.checked && dialogDiagnostic.tripContext
      ? { tripContext: dialogDiagnostic.tripContext } : {}),
  }
  fallbackReport = null
  copyReportButton.hidden = true
  reportPreview.textContent = JSON.stringify(dialogReport, null, 2)
  if (clearStatus) reportStatus.textContent = ''
}

async function sendManualReport(): Promise<void> {
  if (resultsPreviewSession) return
  if (!dialogReport || !dialogDiagnostic || acceptedReportId) return
  const report = dialogReport
  const incidentSequence = dialogDiagnostic.sequence
  const generation = dialogGeneration
  fallbackReport = report
  sendReportButton.disabled = true
  reportIssue.disabled = true
  reportTripContext.disabled = true
  copyReportButton.hidden = true
  reportStatus.textContent = 'Sending report…'
  try {
    const receipt = await submitDiagnostic(report)
    if (generation !== dialogGeneration || !reportDialog.open) return
    if (diagnosticSequence === incidentSequence) {
      latestDiagnosticReceipt = { sequence: incidentSequence, receipt }
      reportSummary.textContent = `Report ${receipt.id} sent.`
    }
    reportStatus.textContent = `Report sent. Share ID ${receipt.id} when asking for help. Received ${formatReportTime(receipt.receivedAt)}.`
    acceptedReportId = receipt.id
    sendReportButton.hidden = true
    copyReportButton.textContent = 'Copy report ID'
    copyReportButton.hidden = false
    requiredElement<HTMLAnchorElement>('#report-support').hidden = false
    const done = requiredElement<HTMLButtonElement>('#report-done')
    done.hidden = false
    done.focus()
  } catch (cause) {
    if (generation !== dialogGeneration || !reportDialog.open) return
    reportStatus.textContent = cause instanceof DiagnosticSubmissionError
      ? cause.message : new DiagnosticSubmissionError().message
    copyReportButton.hidden = false
  } finally {
    if (generation === dialogGeneration && reportDialog.open) {
      sendReportButton.disabled = acceptedReportId !== null
      reportIssue.disabled = acceptedReportId !== null
      reportTripContext.disabled = acceptedReportId !== null
    }
  }
}

async function copyManualReport(): Promise<void> {
  const report = fallbackReport ?? dialogReport
  if (!report) return
  const generation = dialogGeneration
  const id = acceptedReportId
  const current = () => generation === dialogGeneration && reportDialog.open
    && report === (fallbackReport ?? dialogReport) && id === acceptedReportId
  try {
    await navigator.clipboard.writeText(id ?? JSON.stringify(report, null, 2))
    if (!current()) return
    reportStatus.textContent = id
      ? `Report ID ${id} copied. Paste it when asking for help.`
      : 'Report details copied. Paste them when asking for help.'
  } catch {
    if (!current()) return
    reportStatus.textContent = id
      ? `Could not copy report ID. Select and copy it here: ${id}`
      : 'Report details could not be copied.'
  }
}

async function sendAutomaticReport(snapshot: DiagnosticSnapshot, search?: number): Promise<void> {
  if (resultsPreviewSession) return
  if (diagnosticSequence !== snapshot.sequence) return
  const receipt = await submitAutomaticDiagnostic(snapshot.report)
  if (!receipt) return
  if (diagnosticSequence !== snapshot.sequence) return
  latestDiagnosticReceipt = { sequence: snapshot.sequence, receipt }
  reportSummary.textContent = `Recent report: ${receipt.id}`
  if (search !== undefined && search === activeSearch) {
    const notice = document.createElement('p')
    notice.className = 'automatic-report-status'
    notice.textContent = `A private error report was sent. Report ID: ${receipt.id}`
    recommendationList.appendChild(notice)
  }
}

function beginDiagnosticOperation(): number {
  diagnosticSequence += 1
  latestDiagnostic = null
  latestDiagnosticReceipt = null
  reportSummary.textContent = resultsPreviewSession ? 'Reporting disabled for this preview session.' : ''
  return diagnosticSequence
}

function storeDiagnostic(
  sequence: number,
  report: DiagnosticReport,
  tripContext?: DiagnosticReport['tripContext'],
): DiagnosticSnapshot {
  const snapshot = { sequence, report, ...(tripContext ? { tripContext } : {}) }
  if (diagnosticSequence === sequence) {
    latestDiagnostic = snapshot
    if (reportDialog.open && dialogDiagnostic?.sequence === sequence && fallbackReport === null) {
      dialogDiagnostic = snapshot
      refreshDialogReport(false)
    }
  }
  return snapshot
}

function diagnosticReportFor(
  issueCode: DiagnosticIssueCode,
  type: DiagnosticReport['operation']['type'],
  outcome: DiagnosticReport['operation']['outcome'],
  failedPhase: DiagnosticPhase | null,
): DiagnosticReport {
  return {
    schemaVersion: diagnosticSchemaVersion,
    kind: 'automatic',
    issueCode,
    client: clientDiagnosticState(diagnosticLifecycle()),
    operation: {
      type,
      outcome,
      failedPhase,
      errorCodes: [issueCode],
      timings: type === 'search' ? searchProgressPhases().flatMap(({ id, durationMs }) =>
        durationMs === undefined ? [] : [{ phase: id, durationMs: Math.min(120_000, durationMs) }]) : [],
    },
    schedules: {
      subway: scheduleDiagnostic(transit.metadata?.feedVersion, transit.unavailableReason),
      bus: scheduleDiagnostic(bus?.metadata?.feedVersion, bus?.unavailableReason),
      ferry: scheduleDiagnostic(ferry?.metadata?.feedVersion, ferry?.unavailableReason),
      multimodal: scheduleDiagnostic(multimodal?.metadata?.feedVersion, multimodal?.unavailableReason),
    },
  }
}

function diagnosticLifecycle(): DiagnosticReport['client']['state'] {
  if (searchDiagnostic) return searchDiagnostic.state
  return allOriginsSelected() && origins.every((origin) => origin.choice.kind !== 'none') ? 'ready' : 'incomplete'
}

function failedSearchPhase(): DiagnosticPhase | null {
  return searchProgressPhases().find(({ state }) => state === 'failed')?.id ?? null
}

function scheduleDiagnostic(
  identity: string | undefined,
  unavailableReason: string | null | undefined,
): DiagnosticReport['schedules']['subway'] {
  if (identity) return { state: 'ready', identity: identity.slice(0, 128) }
  return { state: unavailableReason ? 'unavailable' : 'not_loaded' }
}

function currentTripContext(): DiagnosticReport['tripContext'] | undefined {
  if (origins.length < minimumOrigins || origins.some((origin) => origin.choice.kind === 'none')) return undefined
  const venueType = document.querySelector<HTMLInputElement>('input[name="venue-type"]:checked')?.value
  if (venueType !== 'coffee' && venueType !== 'brunch' && venueType !== 'drinks') return undefined
  return {
    participantCount: origins.length,
    venueType,
    timeIntent: selectedTimeIntent(),
    travelModes: origins.map((origin) => origin.choice.kind === 'none' ? [] : [...origin.choice.modes]),
  }
}

function formatReportTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

async function initializePlanner(): Promise<void> {
  if (resultsPreviewSession) {
    // Consume readiness failures and refresh editing controls, but never gate
    // synthetic results on the availability of the schedule service.
    void transit.ready.catch(() => undefined).then(() => refreshModeControls())
    reportProblemButton.disabled = true
    reportSummary.textContent = 'Reporting disabled for this preview session.'
    const search = activeSearch
    const { createResultsFixture } = await import('./results-fixture.ts')
    if (activeSearch !== search || clientIncompatible) return
    const time: SearchTime = { ...newYorkDateTime(), intent: 'depart-at' }
    const fixture = createResultsFixture(time)
    if (!await applyPlannerDraft(fixture.draft)) return
    searchedDeparture = time
    requiredElement<HTMLElement>('#search-summary').textContent = '2 people · Coffee · Synthetic preview'
    resultsSheet.setState('partial')
    presentPlaces(fixture.result, selectedOrigins(), 'coffee')
    fixtureRoutePreviews = fixture.routePreviews
    scheduleNote.textContent = 'Synthetic places, travel times and hand-drawn routes. Edit and Find places runs a real search; reporting stays disabled until you open a normal page.'
    completedSearch = activeSearch
    history.pushState({ ...history.state, plannerScreen: 'results', submittedSearch: activeSearch }, '')
    searchOpen = false
    showScreen('results')
    return
  }
  await transit.ready.catch(() => undefined)
  if (clientIncompatible) return
  refreshModeControls()
  const parameters = new URLSearchParams(location.search)
  const encodedDraft = parameters.get('plan')
  if (!encodedDraft) return
  try {
    const draft = decodePlannerDraft(encodedDraft)
    const unchanged = await applyPlannerDraft(draft)
    if (unchanged && parameters.get('run') === '1' && !findButton.disabled) await findPlaces()
  } catch (cause) {
    status.textContent = errorMessage(cause)
  }
}

function initializeSettings(): void {
  settingsButton.addEventListener('click', () => {
    if (!settingsPanel.open) openSettings()
    else closeSettings()
  })
  closeSettingsButton.addEventListener('click', () => closeSettings())
  settingsPanel.addEventListener('click', (event) => {
    if (event.target !== settingsPanel || !settingsPanel.matches(':modal')) return
    const bounds = settingsPanel.getBoundingClientRect()
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) closeSettings()
  })
  settingsPanel.addEventListener('cancel', (event) => {
    event.preventDefault()
    closeSettings()
  })
  document.addEventListener('click', (event) => {
    // A setting can replace its clicked node before the event reaches document.
    const path = event.composedPath()
    if (settingsPanel.open && !settingsPanel.matches(':modal')
      && !path.includes(updateDialog)
      && !path.includes(settingsPanel) && !path.includes(settingsButton)) closeSettings()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && settingsPanel.open && !updateDialog.open && !settingsPanel.matches(':modal')) closeSettings()
  })
  matchMedia('(max-width: 600px), (max-height: 552px)').addEventListener('change', () => closeSettings())
  installDialog.addEventListener('close', () => settingsButton.focus())
}

function trackVisualViewport(): void {
  const refresh = () => {
    const viewport = window.visualViewport
    const left = viewport?.offsetLeft ?? 0
    const width = viewport?.width ?? innerWidth
    // Follow keyboard/browser chrome resizing, but do not reflow the shell on pinch zoom.
    const shellHeight = (viewport?.scale ?? 1) === 1 ? viewport?.height ?? innerHeight : innerHeight
    document.documentElement.style.setProperty('--app-viewport-height', `${shellHeight}px`)
    document.body.classList.toggle('short-viewport', shellHeight < 280)
    requiredElement<HTMLElement>('#page-title').textContent = shellHeight < 280 ? 'Search' : 'Plan meetup'
    // In keyboard-sized space, reserve height for editing instead of a fixed footer.
    const actions = requiredElement<HTMLElement>('#input-actions')
    const actionsParent = requiredElement<HTMLElement>(shellHeight < 280 ? '#input-screen' : '.planner')
    if (actions.parentElement !== actionsParent) actionsParent.appendChild(actions)
    updateSearchToggle()
    document.documentElement.style.setProperty(
      '--visual-viewport-height', `${viewport?.height ?? innerHeight}px`,
    )
    document.documentElement.style.setProperty('--visual-viewport-top', `${viewport?.offsetTop ?? 0}px`)
    document.documentElement.style.setProperty('--visual-viewport-left', `${left}px`)
    document.documentElement.style.setProperty('--visual-viewport-right', `${left + width}px`)
  }
  refresh()
  window.addEventListener('resize', refresh)
  window.visualViewport?.addEventListener('resize', refresh)
  window.visualViewport?.addEventListener('scroll', refresh)
}

function openSettings(): void {
  if (matchMedia('(max-width: 600px), (max-height: 552px)').matches) settingsPanel.showModal()
  else settingsPanel.show()
  settingsButton.setAttribute('aria-expanded', 'true')
  closeSettingsButton.focus()
}

function closeSettings(restoreFocus = true): void {
  if (!settingsPanel.open) return
  settingsPanel.close()
  settingsButton.setAttribute('aria-expanded', 'false')
  if (restoreFocus) settingsButton.focus({ preventScroll: true })
}

function refreshSettingsAvailability(): void {
  appSettings.hidden = installButton.hidden && requiredElement<HTMLButtonElement>('#check-update').hidden
    && requiredElement<HTMLElement>('#install-status').hidden
}

async function applyPlannerDraft(draft: CurrentPlannerDraft): Promise<boolean> {
  while (origins.length < draft.origins.length) addOrigin(false)
  draft.origins.forEach(({ point, choice }, index) => {
    const state = origins[index]
    const form = requiredElement<HTMLFormElement>(`[data-origin-id="${state.id}"]`)
    state.choice = choice
    setChoiceControls(state, form, choice)
    selectOrigin(
      state,
      point,
      requiredChild(form, '.search-results'),
      form.elements.namedItem('query') as HTMLInputElement,
    )
  })
  setCheckedControl('venue-type', draft.venueType)
  departureTime.value = draft.departureTime
  departureEdited = true
  setTimeIntent(draft.leaveNow ? 'leave-now' : draft.timeIntent)
  if (leaveNow) setDepartureSelection(newYorkDateTime())
  updateTimeControlLabels()
  const hydratedSearch = activeSearch

  await Promise.all(selectedBackends().map(async (backend) => {
    if (backend === 'bus') await readyBus().catch(() => undefined)
    if (backend === 'ferry') await readyFerry().catch(() => undefined)
    if (backend === 'multimodal') await readyMultimodal().catch(() => undefined)
  }))
  if (clientIncompatible || activeSearch !== hydratedSearch) return false
  refreshModeControls()
  if (!leaveNow && draft.departureDate) {
    const dates = [...departureDate.options]
      .filter((option) => option.dataset.unavailable !== 'true')
      .map((option) => option.value)
    if (!dates.includes(draft.departureDate)) {
      setDepartureDateOptions(dates, draft.departureDate)
      refreshModeControls()
      throw new Error('Planner draft departure date is unavailable.')
    }
    departureDate.value = draft.departureDate
  }
  return true
}

function setChoiceControls(
  state: OriginState,
  form: HTMLFormElement,
  choice: TravelChoice,
): void {
  form.querySelectorAll<HTMLInputElement>('.travel-mode input').forEach((input) => {
    input.checked = choice.kind !== 'none' && choice.modes.includes(input.value as TravelMode)
  })
  requiredChild<HTMLElement>(form, '.travel-summary').textContent = choice.kind === 'none'
    ? 'Choose at least one' : travelModeOptions.filter((option) => choice.modes.includes(option.value))
      .map((option) => option.label).join(', ')
  updateModeError(form, state)
}

function setCheckedControl(name: 'venue-type', value: string): void {
  const input = document.querySelector<HTMLInputElement>(`input[name="${name}"][value="${value}"]`)
  if (input) input.checked = true
}

function addOrigin(invalidate: boolean): void {
  if (origins.length >= maximumOrigins) return
  const state: OriginState = {
    id: nextOriginId++, point: null,
    choice: { kind: 'permissions', modes: ['walk', 'subway'] }, generation: 0,
  }
  origins.push(state)
  originsContainer.appendChild(createOriginCard(state))
  updateOriginControls()
  refreshSavedPlaceControls()
  if (invalidate) invalidateSearch()
}

function createOriginCard(state: OriginState): HTMLFormElement {
  const form = document.createElement('form')
  form.className = 'origin-card'
  form.dataset.originId = state.id.toString()
  form.innerHTML = `
    <div class="origin-number"></div>
    <div class="origin-heading">
      <label class="origin-label"></label>
      <button class="remove-person" type="button">Remove</button>
    </div>
    <div class="search-row">
      <div class="selected-location-row" hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"></svg>
        <span class="selected-location"></span>
        <button class="change-location" type="button">Change</button>
      </div>
      <input name="query" type="search" placeholder="NYC address or place"
        minlength="3" maxlength="200" required />
      <button type="submit">Search</button>
    </div>
    <div class="search-results" aria-live="polite"></div>
    <div class="quick-add" hidden>
      <span class="quick-add-options"></span>
    </div>
    <p class="location-feedback" id="location-feedback-${state.id}" role="status" hidden></p>
    <details class="travel-options">
      <summary>Travel options <span class="travel-summary">Walking only, Subway</span></summary>
      <fieldset class="travel-mode" aria-describedby="mode-help-${state.id} mode-connections-${state.id}">
        <legend class="visually-hidden">How can they travel?</legend>
        <p class="mode-help" id="mode-help-${state.id}">Choose allowed modes. Transit includes walking.</p>
        <div class="mode-options">
          ${travelModeOptions.map((option) => modeOptionHtml(
            'checkbox', `travel-mode-${state.id}`, option.value, option.label, option.checked,
          )).join('')}
        </div>
        <p class="mode-help mode-connections" id="mode-connections-${state.id}">Bicycles and bike share can connect with selected transit.</p>
        <p class="mode-error" id="mode-error-${state.id}" role="alert" hidden>Choose at least one travel option.</p>
      </fieldset>
    </details>
  `
  const input = form.elements.namedItem('query') as HTMLInputElement
  input.setAttribute('aria-describedby', `location-feedback-${state.id}`)
  requiredChild<HTMLElement>(form, '.selected-location').setAttribute('aria-describedby', `location-feedback-${state.id}`)
  requiredChild<HTMLButtonElement>(form, '.change-location').addEventListener('click', () => {
    if (state.selectionSource?.type === 'device') input.value = ''
    clearOrigin(state, form, input)
    input.focus()
    input.select()
  })
  const modeInputs = form.querySelectorAll<HTMLInputElement>(`input[name="travel-mode-${state.id}"]`)
  form.addEventListener('submit', (event) => void searchOrigin(event, state, form))
  input.addEventListener('input', () => clearOrigin(state, form, input))
  const updateChoice = () => {
    const modes = canonicalPermissions([...modeInputs]
      .filter((mode) => mode.checked).map((mode) => mode.value as TravelMode))
    state.choice = modes.length ? {
      kind: 'permissions',
      modes,
    } : { kind: 'none' }
    requiredChild<HTMLElement>(form, '.travel-summary').textContent = modes.length
      ? travelModeOptions.filter((option) => modes.includes(option.value))
        .map((option) => option.label).join(', ') : 'Choose at least one'
    updateModeError(form, state)
    invalidateSearch()
    const backend = routingBackend(state.choice)
    if (backend === 'bus' && !bus?.metadata) {
      void readyBus().then(() => refreshModeControls()).catch(() => refreshModeControls())
    } else if (backend === 'multimodal' && !multimodal?.metadata) {
      void readyMultimodal().then(() => refreshModeControls()).catch(() => refreshModeControls())
    } else if (backend === 'ferry' && !ferry?.metadata) {
      void readyFerry().then(() => refreshModeControls()).catch(() => refreshModeControls())
    }
  }
  modeInputs.forEach((input) => input.addEventListener('change', updateChoice))
  form.querySelector<HTMLButtonElement>('.remove-person')?.addEventListener('click', () => removeOrigin(state))
  requiredChild<HTMLButtonElement>(form, 'button[type="submit"]').disabled = clientIncompatible
  return form
}

function removeOrigin(state: OriginState): void {
  if (origins.length <= minimumOrigins) return
  const index = origins.indexOf(state)
  if (index < 0) return
  closeSavedPlacePickers()
  state.generation += 1
  cancelLocationRequest(state)
  state.layer?.remove()
  origins.splice(index, 1)
  originsContainer.querySelector(`[data-origin-id="${state.id}"]`)?.remove()
  updateOriginControls()
  refreshSavedPlaceControls()
  refreshOriginMarkers()
  invalidateSearch()
}

function updateOriginControls(): void {
  origins.forEach((state, index) => {
    const form = originsContainer.querySelector<HTMLFormElement>(`[data-origin-id="${state.id}"]`)
    if (!form) return
    const label = personLabel(index)
    form.setAttribute('aria-label', personName(index))
    const input = form.elements.namedItem('query') as HTMLInputElement
    input.id = `origin-${state.id}`
    const labelElement = form.querySelector<HTMLLabelElement>('.origin-label')
    if (labelElement) {
      labelElement.htmlFor = input.id
      labelElement.textContent = personName(index)
    }
    const number = form.querySelector('.origin-number')
    if (number) number.textContent = label
    const remove = form.querySelector<HTMLButtonElement>('.remove-person')
    if (remove) {
      remove.hidden = origins.length <= minimumOrigins
      remove.setAttribute('aria-label', `Remove ${personName(index).toLowerCase()}`)
    }
  })
  addPersonButton.disabled = origins.length >= maximumOrigins
  addPersonButton.textContent = origins.length >= maximumOrigins ? 'Maximum 6 people' : 'Add person'
}

async function searchOrigin(event: SubmitEvent, state: OriginState, form: HTMLFormElement): Promise<void> {
  event.preventDefault()
  if (state.point || clientIncompatible) return
  if (!navigator.onLine) {
    setLocationFeedback(form, 'Connect to the internet to search. You can still use saved places.')
    return
  }
  const input = form.elements.namedItem('query') as HTMLInputElement
  const resultsContainer = requiredChild<HTMLDivElement>(form, '.search-results')
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]')
  if (!input.reportValidity() || !submit) return
  cancelLocationRequest(state)
  setLocationFeedback(form, '')
  refreshSavedPlaceControls()
  const generation = ++state.generation
  const diagnostic = beginDiagnosticOperation()
  submit.disabled = true
  resultsContainer.innerHTML = '<p class="loading">Searching NYC…</p>'

  try {
    const response = await fetch(plannerApiUrl(`/api/geocode?q=${encodeURIComponent(input.value)}`))
    const payload = await plannerApiPayload<{ results?: LocationPoint[]; error?: string }>(response)
    if (!response.ok) throw new Error(payload.error ?? 'Search failed')
    if (generation !== state.generation || !origins.includes(state)) return
    renderLocationResults(state, payload.results ?? [], resultsContainer, input)
  } catch (cause) {
    if (generation !== state.generation || !origins.includes(state)) return
    if (isClientIncompatible(cause)) return
    resultsContainer.innerHTML = `<p class="error">${escapeHtml(errorMessage(cause))}</p>`
    const report = diagnosticReportFor('geocode_failed', 'geocode', 'failed', 'geocoding')
    const snapshot = storeDiagnostic(diagnostic, report)
    void sendAutomaticReport(snapshot)
  } finally {
    submit.disabled = clientIncompatible
  }
}

function locationLabel(displayName: string): { name: string; locality: string } {
  const parts = displayName.split(',').map((part) => part.trim())
  // Keep a leading house number with its street; retain every locality component.
  const name = parts.splice(0, /^\d[\w/-]*$/.test(parts[0]) && parts.length > 1 ? 2 : 1).join(' ')
  return { name, locality: parts.join(', ') }
}

function renderLocationResults(
  state: OriginState,
  results: LocationPoint[],
  container: HTMLDivElement,
  input: HTMLInputElement,
): void {
  if (results.length === 0) {
    container.innerHTML = '<p class="error">No NYC results. Try a neighborhood or full address.</p>'
    return
  }
  const prompt = document.createElement('p')
  prompt.className = 'location-prompt'
  prompt.textContent = 'Choose a starting point'
  container.replaceChildren(prompt, ...results.map((result) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'location-option'
    button.setAttribute('aria-label', result.displayName)
    const label = locationLabel(result.displayName)
    const name = document.createElement('strong')
    name.textContent = label.name
    button.appendChild(name)
    if (label.locality) {
      const locality = document.createElement('span')
      locality.textContent = label.locality
      button.appendChild(locality)
    }
    button.addEventListener('click', () => selectOrigin(state, result, container, input))
    return button
  }))
}

function selectOrigin(
  state: OriginState,
  result: LocationPoint,
  container: HTMLDivElement,
  input: HTMLInputElement,
  selectionSource?: OriginSelectionSource,
  focusSelection = true,
): void {
  state.generation += 1
  cancelLocationRequest(state)
  state.point = result
  state.selectionSource = selectionSource
  setLocationFeedback(container.closest('form')!, selectionSource?.approximate
    ? 'Location is approximate. Enter an address for a more precise start.' : '')
  invalidateSearch()
  input.value = result.displayName
  input.title = result.displayName
  renderSelectedLocation(state, container)
  refreshSavedPlaceControls()
  refreshOriginMarkers()
  if (focusSelection) requiredChild<HTMLButtonElement>(container.closest('form')!, '.change-location').focus()

  const selected = selectedOrigins()
  const insets = mapInsets()
  const usable = map.getContainer().clientWidth - insets.left - insets.right >= 44
    && map.getContainer().clientHeight - insets.top - insets.bottom >= 44
  if (allOriginsSelected()) {
    if (usable) map.fitBounds(selected.map((origin) => [origin.lat, origin.lon]), {
      paddingTopLeft: [insets.left, insets.top], paddingBottomRight: [insets.right, insets.bottom],
      animate: false,
    })
    refreshModeControls()
    status.textContent = ''
  } else {
    if (usable) {
      const point = map.project([result.lat, result.lon], 13)
      point.x += (insets.right - insets.left) / 2
      point.y += (insets.bottom - insets.top) / 2
      map.setView(map.unproject(point, 13), 13, { animate: false })
    }
  }
}

function setLocationFeedback(form: Element, message: string, error = false): void {
  const feedback = requiredChild<HTMLParagraphElement>(form, '.location-feedback')
  feedback.textContent = message
  feedback.classList.toggle('error', error)
  feedback.hidden = !message
}

function cancelLocationRequest(state: OriginState): void {
  // Native permission prompts cannot be canceled. Release admission now; identity checks
  // keep their eventual callbacks from affecting this origin or a newer request.
  if (locationRequest?.origin === state) locationRequest = null
}

function useCurrentLocation(state: OriginState, form: HTMLFormElement): void {
  if (locationRequest || state.point || origins.some((origin) => origin.selectionSource)
    || typeof navigator.geolocation?.getCurrentPosition !== 'function') return
  const request = { origin: state, generation: ++state.generation }
  locationRequest = request
  requiredChild<HTMLDivElement>(form, '.search-results').replaceChildren()
  setLocationFeedback(form, '')
  refreshSavedPlaceControls()
  const finish = (position?: GeolocationPosition, errorCode?: number) => {
    if (locationRequest !== request || state.generation !== request.generation || !origins.includes(state)) return
    locationRequest = null
    // The initiating button was replaced while pending. Recover focus only if
    // the user has not moved to another control in the meantime.
    const recoverFocus = document.activeElement === document.body
    const coordinates = position?.coords
    if (coordinates && Number.isFinite(coordinates.latitude) && Number.isFinite(coordinates.longitude)
      && coordinates.latitude >= NYC_BOUNDS.south && coordinates.latitude <= NYC_BOUNDS.north
      && coordinates.longitude >= NYC_BOUNDS.west && coordinates.longitude <= NYC_BOUNDS.east) {
      selectOrigin(state, {
        displayName: 'Device location', type: 'location', lat: coordinates.latitude, lon: coordinates.longitude,
      }, requiredChild(form, '.search-results'), form.elements.namedItem('query') as HTMLInputElement,
      { type: 'device', approximate: !Number.isFinite(coordinates.accuracy) || coordinates.accuracy > 1_000 }, recoverFocus)
    } else {
      const message = position ? 'Location is outside NYC or invalid. Enter a NYC address.'
        : errorCode === 1 ? 'Location access denied. Allow it in browser settings or enter an address.'
          : errorCode === 3 ? 'Location timed out. Try again or enter an address.'
            : 'Location unavailable. Try again or enter an address.'
      setLocationFeedback(form, message, true)
      refreshSavedPlaceControls()
      if (recoverFocus) requiredChild<HTMLButtonElement>(form, '.use-current-location').focus()
    }
  }
  try {
    navigator.geolocation.getCurrentPosition(
      (position) => finish(position), (error) => finish(undefined, error.code),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    )
  } catch {
    finish()
  }
}

function renderSelectedLocation(state: OriginState, container: HTMLDivElement): void {
  if (!state.point) return
  const form = container.closest('form')!
  const input = form.elements.namedItem('query') as HTMLInputElement
  input.hidden = true
  const location = requiredChild<HTMLElement>(form, '.selected-location')
  location.textContent = state.point.displayName
    .replace(/, United States(?: of America)?$/, '')
    .replace(/(?:, (?:New York(?: City)?|NY))+(, \d{5}(?:-\d{4})?)?$/, '$1')
  requiredChild<SVGElement>(form, '.selected-location-row > svg').innerHTML = state.selectionSource?.type === 'device'
    ? '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/>'
    : '<path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/>'
  requiredChild<HTMLElement>(form, '.selected-location-row').hidden = false
  requiredChild<HTMLButtonElement>(form, 'button[type="submit"]').hidden = true
  container.replaceChildren()
  form.querySelector('.set-saved-place')?.remove()
  if (savedPlaces.some((place) => samePoint(place.point, state.point!))) return
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'set-saved-place'
  button.textContent = 'Save place'
  button.disabled = !savedPlacesWritable
  if (!savedPlacesWritable) {
    const note = document.createElement('p')
    note.className = 'settings-note'
    note.textContent = 'Saving is unavailable on this device.'
    container.appendChild(note)
  }
  button.addEventListener('click', () => showSavePlaceControls(state, container, button))
  requiredChild<HTMLElement>(form, '.origin-heading').insertBefore(button, requiredChild(form, '.remove-person'))
}

function closeSavedPlacePickers(): void {
  document.querySelectorAll<HTMLElement>('.saved-places-popover:popover-open').forEach((popover) => popover.hidePopover())
}

function refreshSavedPlaceControls(): void {
  closeSavedPlacePickers()
  origins.forEach((state) => {
    const form = originsContainer.querySelector<HTMLFormElement>(`[data-origin-id="${state.id}"]`)
    if (!form) return
    const quickAdd = requiredChild<HTMLDivElement>(form, '.quick-add')
    const options = requiredChild<HTMLSpanElement>(quickAdd, '.quick-add-options')
    const available = savedPlaces.filter((place) =>
      !origins.some((origin) => origin.point && samePoint(origin.point, place.point)))
    options.replaceChildren()
    if (available.length && !state.point) {
      const trigger = document.createElement('button')
      trigger.type = 'button'
      trigger.className = 'saved-places-trigger'
      trigger.innerHTML = 'Saved places <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>'
      trigger.setAttribute('aria-expanded', 'false')
      const popover = document.createElement('div')
      popover.className = 'saved-places-popover'
      popover.id = `saved-places-${state.id}`
      popover.popover = 'auto'
      trigger.popoverTargetElement = popover
      trigger.setAttribute('aria-controls', popover.id)
      const position = () => {
        if (!trigger.isConnected || !trigger.checkVisibility()) {
          popover.hidePopover()
          return
        }
        const viewport = window.visualViewport
        const left = (viewport?.offsetLeft ?? 0) + 8
        const top = (viewport?.offsetTop ?? 0) + 8
        const right = left + (viewport?.width ?? window.innerWidth) - 16
        const bottom = top + (viewport?.height ?? window.innerHeight) - 16
        const rect = trigger.getBoundingClientRect()
        if (rect.bottom < top || rect.top > bottom) {
          popover.hidePopover()
          return
        }
        const above = Math.max(0, rect.top - top - 4)
        const below = Math.max(0, bottom - rect.bottom - 4)
        const opensAbove = above > below
        const height = Math.min(228, available.length * 44 + 8, opensAbove ? above : below)
        const width = Math.min(288, right - left)
        popover.style.width = `${width}px`
        popover.style.maxHeight = `${height}px`
        popover.style.left = `${Math.max(left, Math.min(rect.left, right - width))}px`
        popover.style.top = `${opensAbove ? Math.max(top, rect.top - height - 4) : rect.bottom + 4}px`
        popover.style.height = `${height}px`
      }
      popover.addEventListener('beforetoggle', (event) => {
        const open = (event as ToggleEvent).newState === 'open'
        trigger.setAttribute('aria-expanded', String(open))
        if (open) {
          position()
          window.addEventListener('resize', position)
          document.addEventListener('scroll', position, true)
          window.visualViewport?.addEventListener('resize', position)
          window.visualViewport?.addEventListener('scroll', position)
        } else {
          window.removeEventListener('resize', position)
          document.removeEventListener('scroll', position, true)
          window.visualViewport?.removeEventListener('resize', position)
          window.visualViewport?.removeEventListener('scroll', position)
        }
      })
      popover.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        popover.hidePopover()
        trigger.focus({ preventScroll: true })
      })
      popover.replaceChildren(...available.map((place) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.textContent = place.name
        button.title = place.point.displayName
        button.addEventListener('click', () => {
          popover.hidePopover()
          selectOrigin(
            state, place.point, requiredChild(form, '.search-results'),
            form.elements.namedItem('query') as HTMLInputElement,
          )
        })
        return button
      }))
      options.appendChild(trigger)
      options.appendChild(popover)
    }
    if (typeof navigator.geolocation?.getCurrentPosition === 'function'
      && !origins.some((origin) => origin.selectionSource)) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'use-current-location'
      button.setAttribute('aria-describedby', `location-feedback-${state.id}`)
      button.disabled = locationRequest !== null
      button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg>'
      button.append(locationRequest?.origin === state ? 'Locating…' : 'Use my location')
      button.addEventListener('click', () => useCurrentLocation(state, form))
      options.insertBefore(button, options.firstChild)
    }
    quickAdd.hidden = Boolean(state.point) || options.childElementCount === 0
    if (state.point) {
      const canSave = !savedPlaces.some((place) => samePoint(place.point, state.point!))
      // Don't rebuild unchanged selected cards: an asynchronous location result
      // must not discard a place name being entered on another participant.
      if (canSave !== Boolean(form.querySelector('.set-saved-place'))) {
        renderSelectedLocation(state, requiredChild(form, '.search-results'))
      }
    }
  })
}

function samePoint(first: LocationPoint, second: LocationPoint): boolean {
  return first.lat === second.lat && first.lon === second.lon
}

function savedPlaceError(input?: HTMLInputElement): HTMLParagraphElement {
  const error = document.createElement('p')
  error.className = 'saved-place-error error'
  error.id = `saved-place-error-${crypto.randomUUID()}`
  error.setAttribute('role', 'alert')
  if (input) {
    input.setAttribute('aria-describedby', error.id)
    input.addEventListener('input', () => {
      error.textContent = ''
      input.removeAttribute('aria-invalid')
    })
  }
  return error
}

function savedPlaceName(input: HTMLInputElement, error: HTMLElement, currentId?: string): string | null {
  const name = input.value.trim()
  error.textContent = !name ? 'Enter a name.'
    : savedPlaces.some((place) => place.id !== currentId
      && place.name.toLocaleLowerCase() === name.toLocaleLowerCase())
      ? 'Use a different saved place name.' : ''
  input.setAttribute('aria-invalid', String(Boolean(error.textContent)))
  if (error.textContent) {
    input.focus()
    return null
  }
  return input.reportValidity() ? name : null
}

function showSavePlaceControls(
  state: OriginState,
  container: HTMLDivElement,
  trigger: HTMLButtonElement,
): void {
  if (!state.point) return
  trigger.hidden = true
  const controls = document.createElement('div')
  controls.className = 'save-place-controls'
  const input = document.createElement('input')
  input.type = 'text'
  input.maxLength = 80
  input.required = true
  input.placeholder = 'Name'
  input.setAttribute('aria-label', 'Saved place name')
  const error = savedPlaceError(input)
  const save = document.createElement('button')
  save.type = 'button'
  save.textContent = 'Save'
  const cancel = document.createElement('button')
  cancel.type = 'button'
  cancel.textContent = 'Cancel'
  const submit = () => {
    if (!state.point) return
    const name = savedPlaceName(input, error)
    if (!name) return
    const next = [...savedPlaces, { id: crypto.randomUUID(), name, point: state.point }]
    if (updateSavedPlaces(next, error)) {
      renderSelectedLocation(state, container)
      container.closest('form')?.querySelector<HTMLButtonElement>('.change-location')?.focus()
    }
  }
  save.addEventListener('click', submit)
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submit()
    }
  })
  cancel.addEventListener('click', () => {
    controls.remove()
    trigger.hidden = false
    trigger.focus()
  })
  controls.appendChild(input)
  controls.appendChild(save)
  controls.appendChild(cancel)
  controls.appendChild(error)
  container.appendChild(controls)
  input.focus()
}

function updateSavedPlaces(next: SavedPlace[], error: HTMLElement): boolean {
  if (!savedPlacesWritable || !savedPlacesStorage || !persistSavedPlaces(savedPlacesStorage, next)) {
    error.textContent = 'Could not change saved places on this device.'
    return false
  }
  savedPlaces = next
  deletedPlace = null
  renderSavedPlaceSettings()
  refreshSavedPlaceControls()
  refreshSettingsAvailability()
  return true
}

function renderSavedPlaceSettings(): void {
  savedPlacesSetting.hidden = savedPlaces.length === 0 && !deletedPlace && savedPlacesWritable
  savedPlacesList.replaceChildren(...savedPlaces.map((place) => savedPlaceRow(place)))
  const note = requiredElement<HTMLElement>('#saved-places-note')
  note.textContent = !savedPlacesWritable ? 'Saving is unavailable on this device.'
    : savedPlaces.length === 0 ? 'No saved places. Save a starting point while planning.' : ''
  note.hidden = !note.textContent
  requiredElement<HTMLElement>('#saved-place-recovery').hidden = !deletedPlace
  requiredElement<HTMLElement>('#saved-place-deleted').textContent = deletedPlace ? `Deleted ${deletedPlace.place.name}.` : ''
  requiredElement<HTMLElement>('#saved-place-undo-error').textContent = ''
}

function savedPlaceRow(place: SavedPlace): HTMLDivElement {
  const row = document.createElement('div')
  row.className = 'saved-place-row'
  const error = savedPlaceError()
  const copy = document.createElement('div')
  copy.className = 'saved-place-copy'
  const name = document.createElement('strong')
  name.textContent = place.name
  const address = document.createElement('span')
  address.textContent = place.point.displayName
  copy.appendChild(name)
  copy.appendChild(address)
  const actions = document.createElement('div')
  actions.className = 'saved-place-actions'
  const rename = document.createElement('button')
  rename.type = 'button'
  rename.textContent = 'Rename'
  rename.setAttribute('aria-label', `Rename ${place.name}`)
  rename.disabled = !savedPlacesWritable
  rename.addEventListener('click', () => showRenameControls(place, row))
  const remove = document.createElement('button')
  remove.type = 'button'
  remove.textContent = 'Delete'
  remove.setAttribute('aria-label', `Delete ${place.name}`)
  remove.setAttribute('aria-describedby', error.id)
  remove.disabled = !savedPlacesWritable
  remove.addEventListener('click', () => {
    const index = savedPlaces.findIndex((candidate) => candidate.id === place.id)
    if (!updateSavedPlaces(savedPlaces.filter((candidate) => candidate.id !== place.id), error)) return
    deletedPlace = { place, index }
    renderSavedPlaceSettings()
    requiredElement<HTMLButtonElement>('#undo-delete-place').focus()
  })
  actions.appendChild(rename)
  actions.appendChild(remove)
  row.appendChild(copy)
  row.appendChild(actions)
  row.appendChild(error)
  return row
}

function showRenameControls(place: SavedPlace, row: HTMLDivElement): void {
  const input = document.createElement('input')
  input.type = 'text'
  input.value = place.name
  input.maxLength = 80
  input.required = true
  input.setAttribute('aria-label', `New name for ${place.name}`)
  const error = savedPlaceError(input)
  const actions = document.createElement('div')
  actions.className = 'saved-place-actions'
  const save = document.createElement('button')
  save.type = 'button'
  save.textContent = 'Save'
  const cancel = document.createElement('button')
  cancel.type = 'button'
  cancel.textContent = 'Cancel'
  const focusRename = () => {
    const index = savedPlaces.findIndex((candidate) => candidate.id === place.id)
    savedPlacesList.children[index]?.querySelector<HTMLButtonElement>('button')?.focus()
  }
  const submit = () => {
    const nextName = savedPlaceName(input, error, place.id)
    if (!nextName) return
    if (updateSavedPlaces(savedPlaces.map((candidate) =>
      candidate.id === place.id ? { ...candidate, name: nextName } : candidate), error)) focusRename()
  }
  save.addEventListener('click', submit)
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submit()
    }
  })
  cancel.addEventListener('click', () => {
    renderSavedPlaceSettings()
    focusRename()
  })
  actions.appendChild(save)
  actions.appendChild(cancel)
  row.replaceChildren(input, actions, error)
  input.focus()
}

async function findPlaces(retry?: AreaRecovery): Promise<void> {
  if (clientIncompatible || !navigator.onLine) return
  if (retry && (retry !== areaRecovery || retry.pending || retry.selectedIndex === null)) return
  const selectedArea = retry && retry.selectedIndex !== null ? retry.result.areas[retry.selectedIndex] : null
  const points = retry ? retry.request.participants.map(({ point }) => point) : selectedOrigins()
  if (points.length !== origins.length) return
  searchController?.abort(new Error('Superseded by a newer search.'))
  const controller = new AbortController()
  searchController = controller
  const search = ++activeSearch
  const tripContext = retry ? retry.tripContext : currentTripContext()
  let diagnosticIssue: DiagnosticIssueCode | null = null
  try {
    searchedDeparture = retry ? retry.request.time : captureDepartureForSearch()
  } catch (cause) {
    status.classList.remove('visually-hidden')
    status.textContent = errorMessage(cause)
    return
  }
  fixtureRoutePreviews = null
  const diagnostic = beginDiagnosticOperation()
  if (!retry) {
    areaRecovery = null
    clearResultPresentation()
    recommendationList.replaceChildren()
  } else retry.pending = true
  lastBikeShareUpdatedAt = null
  const type = retry?.request.venueType ?? (document.querySelector<HTMLInputElement>('input[name="venue-type"]:checked')?.value
    ?? 'coffee') as SearchRequest['venueType']
  const request: SearchRequest = retry && selectedArea ? {
    ...retry.request,
    area: { center: { lat: selectedArea.lat, lon: selectedArea.lon }, radius: retry.result.radius },
  } : {
    participants: origins.map((origin, index) => {
      if (origin.choice.kind !== 'permissions') throw new Error('Choose a travel option for each person.')
      return { point: points[index], modes: origin.choice.modes }
    }),
    time: searchedDeparture,
    discoveryTime: discoveryDeparture(searchedDeparture, availableDates()),
    scheduleAvailable: !departureDate.disabled,
    venueType: type,
    preference: rankingPreference,
  }

  searchDiagnostic = { state: 'searching' }
  completedSearch = null
  if (!retry) {
    pendingMapBounds = points.map((point) => [point.lat, point.lon])
    resultsSheet.setState('partial')
  }
  requiredElement<HTMLElement>('#search-summary').textContent = `${origins.length} people · ${
    type === 'coffee' ? 'Coffee' : type === 'brunch' ? 'Brunch' : 'Drinks'
  }`
  renderSearchedDeparture()
  if (history.state?.plannerScreen === 'input') history.pushState({ ...history.state, plannerScreen: 'loading', submittedSearch: search }, '')
  else history.replaceState({ ...history.state, plannerScreen: 'loading', submittedSearch: search }, '')
  searchOpen = true
  showScreen('loading')
  findButton.disabled = true
  findButton.textContent = 'Finding places…'
  requiredElement<HTMLButtonElement>('#search-area').disabled = true
  startSearchProgress(
    search,
    origins.some(requiresFinalTravelTimes),
    origins.some((origin) => origin.choice.kind === 'permissions'
      && scheduledModesFor(origin.choice).length > 0),
  )

  const retainAreas = (message: string): void => {
    if (!retry) return
    searchDiagnostic = areaSearchDiagnostic('venue_unavailable', retry.result.areas, retry.result.people, message)
    lastBikeShareUpdatedAt = retry.result.bikeShareUpdatedAt
    status.textContent = message
  }

  try {
    const result = await searchPlaces(request, {
      createRouting: (snapshot, signal) => createSearchRouting(snapshot, {
        transit, readyBus, readyFerry, readyMultimodal, roadMatrix, bikeShareSnapshot,
      }, signal),
      venues: searchVenues,
    }, controller.signal, (event) => {
      if (search !== activeSearch || controller.signal.aborted) return
      if ('failed' in event) {
        failSearchProgress()
        if (!retry && searchProgress) {
          searchProgress.phases.push({
            id: 'travel-times', activeLabel: 'Checking meeting areas', completeLabel: 'Checked meeting areas', state: 'pending',
          })
          advanceSearchPhase('travel-times')
          status.textContent = 'Places couldn’t be loaded. Checking meeting areas…'
        }
      }
      else if (searchProgress?.phases.some((phase) => phase.id === event.phase)) advanceSearchPhase(event.phase)
    })
    if (search !== activeSearch || controller.signal.aborted) return
    const routedPeople = result.people
    lastBikeShareUpdatedAt = result.bikeShareUpdatedAt
    if (result.kind === 'areas') {
      const message = errorMessage(result.venueError)
      diagnosticIssue = 'venue_unavailable'
      areaRecovery = { request: structuredClone(request), result, tripContext, selectedIndex: null, pending: false }
      renderSearchAreas(result.areas, result.radius, points, routedPeople, message, true)
      finishSearchProgress()
      searchDiagnostic = areaSearchDiagnostic('venue_unavailable', result.areas, routedPeople, message)
      hasResults = true
      routingMode.textContent = routingDescription(routedPeople)
      scheduleNote.textContent = `${scheduleDescription()} Nearby places could not be loaded; these are candidate areas, not place recommendations.`
      status.textContent = searchAreaStatus(result.areas.length, 'Nearby places unavailable.')
      return
    }
    if (retry && (result.kind === 'empty' || !result.venues.length)) {
      finishSearchProgress()
      const routingFailed = routedPeople.some((person) => person.failures.length)
      if (routingFailed) diagnosticIssue = 'routing_degraded'
      retainAreas(routingFailed
        ? 'Travel times couldn’t be checked. Try this area again or select another.'
        : 'No places fit these trips in this area. Select another area or try again.')
      return
    }
    if (result.kind === 'empty') {
      finishSearchProgress()
      if (result.reason === 'no_shared_area') {
        status.textContent = 'No meeting area fits this time. Change time or travel options.'
      } else {
        status.textContent = 'No trip fits everyone’s options. Review starting points and travel options.'
      }
      requiredElement<HTMLElement>('#edit-empty-search').hidden = false
      if (result.reason === 'no_final_routes') {
        if (routedPeople.some((person) => person.failures.length)) diagnosticIssue = 'routing_degraded'
        searchDiagnostic = {
          state: 'empty', code: 'no_places_fit', venueCandidates: result.venueCandidates ?? 0,
          routing: routingDiagnostics(routedPeople), results: [],
        }
      } else {
        searchDiagnostic = { state: 'empty', code: 'no_shared_transit_area', routing: routingDiagnostics(routedPeople) }
      }
      return
    }
    if (result.degraded) diagnosticIssue = 'routing_degraded'
    presentPlaces(result, points, type, retry)
  } catch (cause) {
    if (search !== activeSearch) return
    if (isClientIncompatible(cause)) {
      clientIncompatible = true
      searchDiagnostic = null
      return
    }
    const message = errorMessage(cause)
    diagnosticIssue = 'search_failed'
    searchDiagnostic = { state: 'error', code: 'search_failed', message }
    failSearchProgress()
    if (retry) {
      retainAreas('Places couldn’t be loaded in this area. Try again or select another area.')
      return
    }
    resultsError.textContent = message
    resultsError.hidden = false
    status.textContent = 'Search failed. Try again.'
  } finally {
    if (search === activeSearch) {
      if (retry) retry.pending = false
      status.classList.remove('status-searching')
      refreshModeControls()
      if (clientIncompatible) return
      completedSearch = search
      requiredElement<HTMLButtonElement>('#retry-search').hidden = !!retry || searchDiagnostic?.state !== 'error'
      history.replaceState({ ...history.state, plannerScreen: 'results', submittedSearch: search }, '')
      searchOpen = false
      showScreen('results')
      const report = diagnosticReportFor(
        diagnosticIssue ?? 'other_problem',
        'search',
        diagnosticIssue === 'venue_unavailable' ? 'partial'
          : diagnosticIssue === 'routing_degraded' ? 'degraded'
            : diagnosticIssue === 'search_failed' ? 'failed' : 'other',
        failedSearchPhase(),
      )
      const snapshot = storeDiagnostic(diagnostic, report, tripContext)
      if (diagnosticIssue) void sendAutomaticReport(snapshot, search)
    }
  }
}

function presentPlaces(
  result: Extract<SearchResult, { kind: 'places' }>,
  points: LocationPoint[],
  type: SearchRequest['venueType'],
  retry?: AreaRecovery,
): void {
  const { venues: ranked, degraded, people } = result
  areaRecovery = null
  renderVenues(ranked, points, people)
  if (retry) {
    const scope = requiredElement<HTMLElement>('#search-scope')
    scope.textContent = `Places in Area ${retry.selectedIndex! + 1}`
    scope.hidden = false
  }
  if (degraded) {
    resultsError.textContent = 'Some permitted routes were unavailable; recommendations compare the remaining options.'
    resultsError.hidden = false
  }
  finishSearchProgress()
  searchDiagnostic = {
    state: ranked.length ? 'results' : 'empty',
    code: ranked.length ? 'places_found' : 'no_places_fit',
    venueCandidates: result.venueCandidates,
    routing: routingDiagnostics(people),
    results: ranked.map(({ id, name, type, minutes, range }) => ({ id, name, type, minutes, range })),
  }
  hasResults = true
  routingMode.textContent = routingDescription(people)
  scheduleNote.textContent = `${scheduleDescription()} Places within five minutes rank together; `
    + `reported wheelchair access breaks ties.${type === 'brunch'
    ? ' Brunch results use mapped breakfast and brunch tags; check menus and hours.' : ''}`
  requiredElement<HTMLDetailsElement>('.estimate-notes').open = degraded
  status.textContent = ranked.length
    ? `${ranked.length} place${ranked.length === 1 ? '' : 's'} found.`
    : type === 'brunch' ? 'No brunch places fit these trips. Try another category.' : 'No places fit these trips. Try another category.'
}

function routingDiagnostics(people: RoutedParticipant[]): RoutingDiagnostic[] {
  return people.map((person, index) => ({
    person: personLabel(index),
    choice: origins[index].choice,
    reachableEndpoints: Math.max(0, ...person.alternatives.map((alternative) =>
      'origin' in alternative ? 1
        : isScheduledPerson(alternative) ? alternative.result.journeys.filter(Boolean).length
          : isBikeSharePerson(alternative) ? alternative.journeys.filter(Boolean).length
            : alternative.durations.filter((duration) => duration !== null).length)),
    totalEndpoints: Math.max(0, ...person.alternatives.map((alternative) =>
      'origin' in alternative ? 1
        : isScheduledPerson(alternative) ? alternative.endpoints.length
          : isBikeSharePerson(alternative) ? alternative.journeys.length : alternative.durations.length)),
  }))
}

function googleMapsPlaceUrl(venue: Venue): string {
  const address = venue.address?.trim()
  const query = address
    ? `${venue.name}, ${address}, New York, NY`
    : `${venue.name}, ${venue.lat},${venue.lon}`
  const url = new URL('https://www.google.com/maps/search/')
  url.searchParams.set('api', '1')
  url.searchParams.set('query', query)
  return url.toString()
}

function renderVenues(
  venues: PlannedVenue[],
  points: LocationPoint[],
  routedPeople: RoutedParticipant[] | null,
): void {
  resultsTitle.textContent = 'Places'
  renderSearchedDeparture()
  clearResultPresentation()
  requiredElement<HTMLElement>('#ranking-note').hidden = venues.length < 2
  if (venues.length === 0) {
    requiredElement<HTMLElement>('#edit-empty-search').hidden = false
    return
  }

  const cards: HTMLButtonElement[] = []
  const details: HTMLDivElement[] = []
  const results: HTMLElement[] = []
  const markers: Marker[] = []
  let selectedVenueIndex: number | null = null
  let expandedVenueIndex: number | null = null

  venues.forEach((venue, index) => {
    const result = document.createElement('article')
    result.className = 'venue-result'
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'venue-card'
    button.id = `venue-trigger-${activeSearch}-${index}`
    const detailedRouteLines = venue.journeys && routedPeople
      ? venue.journeys.map((journey, personIndex) =>
        `<section class="person-journey">
          <div class="participant-heading">
            <h5 class="participant-origin" title="${escapeHtml(points[personIndex].displayName)}">
              <span class="participant-label">${personLabel(personIndex)}</span>
              <span>${escapeHtml(locationLabel(points[personIndex].displayName).name)}</span>
            </h5>
            <div class="participant-times">
              <strong class="participant-duration">${estimatedJourney(journey) ? '~' : ''}${tripMinutes(journey, venue.minutes[personIndex])} min trip</strong>
              ${walkingSummaryHtml(journey)}
            </div>
          </div>
          <div class="participant-route">${routeSummaryHtml(personLabel(personIndex), routedPeople[personIndex], journey)}</div>
          <p class="journey-time">${formatJourneyTime(journey, venue.minutes[personIndex] * 60)}</p>
        </section>`).join('')
      : ''
    const compactParticipants = venue.journeys && routedPeople
      ? venue.journeys.map((journey, personIndex) =>
        `<div class="participant-summary">
          <div class="participant-heading">
            <span class="participant-label" title="${escapeHtml(points[personIndex].displayName)}">${personLabel(personIndex)}</span>
            <div class="participant-times">
              <strong class="participant-duration">${estimatedJourney(journey) ? '~' : ''}${tripMinutes(journey, venue.minutes[personIndex])} min<span class="trip-label"> trip</span></strong>
              ${walkingSummaryHtml(journey)}
            </div>
          </div>
          <div class="participant-route">${routeSummaryHtml(personLabel(personIndex), routedPeople[personIndex], journey, 'compact')}</div>
        </div>`).join('')
      : venue.minutes.map((minutes, personIndex) => `
        <div class="participant-summary">
          <div class="participant-heading">
            <span class="participant-label" title="${escapeHtml(points[personIndex].displayName)}">${personLabel(personIndex)}</span>
            <strong class="participant-duration">${minutes} min<span class="trip-label"> trip</span></strong>
          </div>
        </div>
      `).join('')
    const accessibility = venue.wheelchair === 'yes'
      ? 'Wheelchair accessible'
      : venue.wheelchair === 'limited' ? 'Limited wheelchair access'
        : venue.wheelchair === 'no' ? 'Not wheelchair accessible' : ''
    button.innerHTML = `
      <div class="venue-heading">
        <span class="venue-rank">${index + 1}</span>
        <div class="venue-identity">
          <div class="venue-name"><h3>${escapeHtml(venue.name)}</h3><span>${escapeHtml(venue.type)}</span></div>
          ${accessibility ? `<p class="venue-feature">${accessibility}</p>` : ''}
        </div>
        <svg class="venue-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m6 9 6 6 6-6"></path>
        </svg>
      </div>
      <div class="participant-comparisons">${compactParticipants}</div>
      <span class="journey-disclosure">View details</span>
    `
    const detail = document.createElement('div')
    detail.id = `venue-details-${activeSearch}-${index}`
    detail.className = 'venue-details'
    const venueAddress = venue.address?.trim()
    detail.innerHTML = `
      <section class="venue-place-details">
        <h4 class="visually-hidden">About this place</h4>
        ${venueAddress ? `<p class="venue-address">${escapeHtml(venueAddress)}</p>` : ''}
        <div class="venue-place-action-row">
          <span>Photos &amp; place details</span>
          <span class="venue-place-action"></span>
        </div>
      </section>
      <section class="venue-journeys">
        <h4 class="visually-hidden">Getting there</h4>
        ${detailedRouteLines ? `<div class="route-summary route-summary-expanded">${detailedRouteLines}</div>` : ''}
      </section>
      <p class="route-preview-status" role="status" aria-live="polite"></p>
    `
    const mapsLink = document.createElement('a')
    mapsLink.className = 'venue-place-link'
    mapsLink.href = googleMapsPlaceUrl(venue)
    mapsLink.target = '_blank'
    mapsLink.rel = 'noreferrer'
    mapsLink.innerHTML = `
      <svg class="venue-place-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m9 18-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z"></path>
        <path d="M9 3v15M15 6v15"></path>
      </svg>
      <span>Google Maps</span>
    `
    const externalCue = document.createElement('span')
    externalCue.className = 'venue-place-external'
    externalCue.setAttribute('aria-hidden', 'true')
    externalCue.textContent = '↗'
    mapsLink.appendChild(externalCue)
    const externalDescription = document.createElement('span')
    externalDescription.className = 'visually-hidden'
    externalDescription.textContent = ' (opens in another app or tab)'
    mapsLink.appendChild(externalDescription)
    requiredChild<HTMLElement>(detail, '.venue-place-action').appendChild(mapsLink)
    const popupTimes = venue.minutes.map((minutes, personIndex) =>
      `${personLabel(personIndex)}: ${estimatedJourney(venue.journeys?.[personIndex]) ? '~' : ''}${tripMinutes(venue.journeys?.[personIndex], minutes)} min trip`).join(' · ')
    const marker = L.marker([venue.lat, venue.lon], {
      icon: venueIcon(index + 1),
      title: `${index + 1}. ${venue.name}`,
      zIndexOffset: 2_000,
    })
      .bindPopup(`<strong>${escapeHtml(venue.name)}</strong><br>${popupTimes}`, { autoPan: false })
      .addTo(map)

    const activateVenue = (source: 'card' | 'marker'): void => {
      if (screen !== 'results' || searchOpen) return
      const selectionChanged = selectedVenueIndex !== index
      if (source === 'card') expandedVenueIndex = expandedVenueIndex === index ? null : index
      else if (selectionChanged) expandedVenueIndex = null
      selectedVenueIndex = index
      synchronizeVenueDisclosure?.()
      if (compactResults.matches) {
        if (source === 'marker') resultsSheet.setState('partial')
        else if (expandedVenueIndex !== null) resultsSheet.setState('full')
      }
      if (source === 'marker' || expandedVenueIndex === index) requestAnimationFrame(() => revealVenueCard(button))
      if (!selectionChanged) {
        if (source === 'marker') {
          if (compactResults.matches) map.closePopup()
          else marker.openPopup()
        }
        return
      }
      positionSelectedPlace([venue.lat, venue.lon], 16)
      if (compactResults.matches) map.closePopup()
      else marker.openPopup()
      void showRoutePreview(
        requiredChild<HTMLParagraphElement>(detail, '.route-preview-status'),
        venue,
        points,
        routedPeople,
      )
    }
    button.addEventListener('click', () => activateVenue('card'))
    marker.on('click', () => activateVenue('marker'))
    marker.on('keypress', (event: LeafletKeyboardEvent) => {
      if (event.originalEvent.key === 'Enter') activateVenue('marker')
    })

    result.appendChild(button)
    result.appendChild(detail)
    cards.push(button)
    details.push(detail)
    results.push(result)
    markers.push(marker)
    venueLayers.push(marker)
  })

  synchronizeVenueDisclosure = () => {
    cards.forEach((button, index) => {
      const selected = index === selectedVenueIndex
      const expanded = index === expandedVenueIndex
      results[index].classList.toggle('venue-result-selected', selected)
      results[index].classList.toggle('venue-result-expanded', expanded)
      markers[index].getElement()?.classList.toggle('venue-marker-selected', selected)
      markers[index].setZIndexOffset(selected ? 3_000 : 2_000)
      button.setAttribute('aria-expanded', String(expanded))
      button.setAttribute('aria-controls', details[index].id)
      requiredChild<HTMLElement>(button, '.participant-comparisons').hidden = expanded
      details[index].hidden = !expanded
    })
  }
  synchronizeVenueDisclosure()
  recommendationList.replaceChildren(...results)
  requiredElement<HTMLElement>('#places-count').textContent = `${venues.length} place${venues.length === 1 ? '' : 's'}`

  pendingMapBounds = [
    ...points.map((point) => [point.lat, point.lon] as [number, number]),
    ...venues.slice(0, 5).map((venue) => [venue.lat, venue.lon] as [number, number]),
  ]
}

function revealVenueCard(button: HTMLButtonElement): void {
  if (screen !== 'results' || searchOpen || !recommendationsPanel.clientHeight) return
  recommendationsPanel.scrollTop += button.getBoundingClientRect().top
    - recommendationsPanel.getBoundingClientRect().top - 8
}

function clearResultPresentation(): void {
  clearRoutePreview()
  requiredElement<HTMLElement>('#edit-empty-search').hidden = true
  requiredElement<HTMLElement>('#ranking-note').hidden = true
  requiredElement<HTMLElement>('#area-search-actions').hidden = true
  requiredElement<HTMLElement>('#search-scope').hidden = true
  venueLayers.forEach((layer) => layer.remove())
  venueLayers = []
  synchronizeVenueDisclosure = null
  pendingSelection = null
  pendingMapBounds = null
  resultsError.className = 'error'
  resultsError.textContent = ''
  resultsError.hidden = true
  requiredElement<HTMLElement>('#places-count').textContent = 'Places'
  recommendationsPanel.scrollTop = 0
}

interface PreviewLeg {
  profile: RoadProfile
  from: Pick<LocationPoint, 'lat' | 'lon'>
  to: Pick<LocationPoint, 'lat' | 'lon'>
  expectedSeconds: number
}

function showRouteTransitions(venue: PlannedVenue, people: RoutedParticipant[]): void {
  const groups = new Map<string, ReturnType<typeof routeTransitions>>()
  venue.journeys?.forEach((journey, personIndex) => {
    for (const transition of routeTransitions(journey, people[personIndex])) {
      const key = `${transition.lat},${transition.lon}`
      const group = groups.get(key) ?? []
      group.push({ ...transition, label: `${personLabel(personIndex)}: ${transition.label}` })
      groups.set(key, group)
    }
  })
  for (const group of groups.values()) {
    const first = group[0]
    const mode = group.every((event) => event.mode === first.mode) ? first.mode : 'transfer'
    const icon = mode === 'transfer'
      ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 8h16m-4-4 4 4-4 4M20 16H4m4-4-4 4 4 4"/></svg>'
      : modeIcon(mode)
    const content = document.createElement('div')
    content.className = 'route-transition-popup'
    for (const event of group) {
      const line = document.createElement('p')
      line.textContent = event.label
      content.appendChild(line)
    }
    const marker = L.marker([first.lat, first.lon], {
      keyboard: false, autoPanOnFocus: false, zIndexOffset: 1_000,
      icon: L.divIcon({
        className: 'route-transition-marker',
        html: `<span class="route-transition-badge">${icon}</span>`,
        iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -12],
      }),
    }).bindPopup(content, { autoPan: false, className: 'route-transition-detail' }).addTo(map)
    previewLayers.push(marker)
    const element = marker.getElement()!
    element.setAttribute('role', 'button')
    element.setAttribute('aria-label', group.map((event) => event.label).join('; '))
    element.setAttribute('aria-expanded', 'false')
    element.inert = screen !== 'results' || searchOpen
    element.tabIndex = element.inert ? -1 : 0
    element.style.pointerEvents = element.inert ? 'none' : ''
    element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        event.stopPropagation()
        marker.togglePopup()
      } else if (event.key === 'Escape') {
        marker.closePopup()
      }
    })
    marker.on('click', () => element.focus({ preventScroll: true }))
    marker.on('popupopen', () => {
      element.setAttribute('aria-expanded', 'true')
      const popup = marker.getPopup()!.getElement()!
      popup.querySelector('.leaflet-popup-close-button')?.addEventListener('click', () => element.focus({ preventScroll: true }), { once: true })
      popup.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          marker.closePopup()
          element.focus({ preventScroll: true })
        }
      })
    })
    marker.on('popupclose', () => element.setAttribute('aria-expanded', 'false'))
  }
}

function routeColor(personIndex: number): string {
  const colors = document.documentElement.dataset.theme === 'dark'
    ? ['#f09a72', '#74c5d5', '#bda1e8', '#91cc9c', '#e2b66c', '#ef9fba']
    : ['#c65d35', '#287d8e', '#7456a4', '#3f7f4c', '#9a5b1f', '#b33f62']
  return colors[personIndex % colors.length]
}

function addRoutePreview(coordinates: L.LatLngExpression[], personIndex: number, options: L.PolylineOptions): void {
  const layer = L.polyline(coordinates, { ...options, color: routeColor(personIndex) }).addTo(map)
  previewPeople.set(layer, personIndex)
  previewLayers.push(layer)
}

async function showRoutePreview(
  status: HTMLParagraphElement,
  venue: PlannedVenue,
  points: LocationPoint[],
  people: RoutedParticipant[] | null,
): Promise<void> {
  clearRoutePreview()
  status.classList.remove('visually-hidden')
  if (clientIncompatible) {
    status.textContent = 'Route preview unavailable for this app version.'
    return
  }
  if (!people || !venue.journeys) return
  const controller = new AbortController()
  previewController = controller
  const generation = previewGeneration
  const search = activeSearch
  showRouteTransitions(venue, people)
  if (fixtureRoutePreviews) {
    const routes = fixtureRoutePreviews[venue.id]
    routes?.forEach(({ coordinates, personIndex, walking }) => {
      addRoutePreview(coordinates, personIndex, {
        weight: 5, opacity: 0.82,
        dashArray: walking ? '6 7' : undefined,
        className: walking ? 'walking-route-preview' : 'scheduled-route-preview',
      })
    })
    status.classList.toggle('visually-hidden', Boolean(routes?.length))
    status.textContent = routes?.length ? 'Routes shown on the map.' : 'Synthetic route preview unavailable.'
    return
  }
  let rendered = 0
  let gaps = 0
  let busy = false
  let attempted = false
  const current = () => !clientIncompatible && !controller.signal.aborted
    && generation === previewGeneration && search === activeSearch
  const scheduledLegs = venue.journeys.flatMap((journey, personIndex) => {
    if (journey.kind !== 'scheduled') return []
    const person = scheduledAlternatives(people[personIndex]).find((candidate) =>
      candidate.arriveByJourneys?.some((arriveBy) => arriveBy === journey.journey)
      || candidate.result.journeys[journey.endpointIndex] === journey.journey)
    return journey.journey.legs.flatMap((leg) => leg.kind === 'ride' ? [{
      identity: leg.geometryIdentity,
      fromStopIndex: leg.fromStopIndex,
      toStopIndex: leg.toStopIndex,
      feedVersion: person?.feedVersion,
      date: person?.result.date,
      personIndex,
    }] : [])
  })
  const roadLegs = venue.journeys.flatMap((journey, personIndex) =>
    previewStreetLegs(journey, people[personIndex], points[personIndex], venue)
      .map((leg) => ({ ...leg, personIndex })))
  const notice = requiredElement<HTMLElement>('#route-recovery')
  const message = requiredElement<HTMLElement>('#route-recovery-message')
  const retry = requiredElement<HTMLButtonElement>('#retry-routes')
  const notices = requiredElement<HTMLElement>('#results-notices')
  const completion = requiredElement<HTMLElement>('#route-completion')
  refreshRouteRecovery = () => {
    const missing = gaps + roadLegs.length + scheduledLegs.length
    const hadFocus = !notice.hidden && document.activeElement === notices
    notice.hidden = searchOpen || !current() || (!busy && missing === 0)
    message.textContent = notice.hidden ? '' : status.textContent
    completion.textContent = current() && !searchOpen && !busy && rendered && missing === 0 ? 'Available route segments loaded.' : ''
    retry.hidden = busy ? !attempted : roadLegs.length + scheduledLegs.length === 0
    retry.disabled = busy || !current() || !navigator.onLine
    notice.inert = screen !== 'results' || searchOpen || !current()
    refreshResultsNoticeFocus()
    if (hadFocus && notice.hidden && current() && screen === 'results' && !searchOpen) {
      requiredElement<HTMLButtonElement>('#edit-search').focus({ preventScroll: true })
    }
  }
  const runPass = async (): Promise<void> => {
    if (busy || !current()) return
    if (document.activeElement === retry) notices.focus({ preventScroll: true })
    busy = true
    // Freeze each admission: failures are eligible only for the next explicit pass.
    const scheduledBatch = scheduledLegs.splice(0)
    const roadBatch = roadLegs.splice(0, 12)
    status.classList.remove('visually-hidden')
    status.textContent = 'Loading routes…'
    refreshRouteRecovery?.()
    await Promise.all(scheduledBatch.map(async (leg) => {
      if (leg.identity === undefined || leg.fromStopIndex === undefined || leg.toStopIndex === undefined
        || leg.feedVersion === undefined || leg.date === undefined) {
        gaps += 1
        return
      }
      try {
        const coordinates = await scheduledGeometry(
          leg.identity, leg.fromStopIndex, leg.toStopIndex,
          { feedVersion: leg.feedVersion, date: leg.date }, controller.signal,
        )
        if (!current()) return
        if (!coordinates) {
          gaps += 1
          return
        }
        addRoutePreview(coordinates, leg.personIndex, {
          weight: 6, opacity: 0.9,
          className: 'scheduled-route-preview',
        })
        rendered += 1
      } catch {
        if (current()) scheduledLegs.push(leg)
      }
    }))
    if (!current()) return
    for (const leg of roadBatch) {
      try {
        const geometry = await cachedRoadGeometry(leg, controller.signal)
        if (!current()) return
        addRoutePreview(geometry.coordinates, leg.personIndex, {
          weight: 5, opacity: 0.82,
          dashArray: leg.profile === 'foot' ? '6 7' : undefined,
          className: leg.profile === 'foot' ? 'walking-route-preview' : 'road-route-preview',
        })
        rendered += 1
      } catch (cause) {
        if (!current() || isClientIncompatible(cause)) return
        roadLegs.push(leg)
      }
    }
    busy = false
    attempted = true
    const unavailable = gaps + roadLegs.length + scheduledLegs.length
    status.classList.toggle('visually-hidden', unavailable === 0 && rendered > 0)
    status.textContent = unavailable
      ? `${unavailable} route segment${unavailable === 1 ? '' : 's'} unavailable; gaps are not approximated.`
      : rendered ? 'Available route segments shown on the map.' : 'Route preview is not available for this trip.'
    refreshRouteRecovery?.()
  }
  retryRoutePreview = runPass
  await runPass()
}

function previewStreetLegs(
  journey: EndpointJourney,
  participant: RoutedParticipant,
  origin: LocationPoint,
  venue: Venue,
): PreviewLeg[] {
  if (journey.kind === 'walk') return [{
    profile: 'foot', from: origin, to: venue, expectedSeconds: journey.durationSeconds,
  }]
  if (journey.kind === 'road') {
    return [{
      profile: journey.mode === 'taxi' ? 'car' : 'bike', from: origin, to: venue,
      expectedSeconds: journey.durationSeconds - (journey.mode === 'taxi' ? 300 : 0),
    }]
  }
  if (journey.kind === 'bike-share') return [
    { profile: 'foot', from: origin, to: journey.pickupStation, expectedSeconds: journey.accessSeconds },
    { profile: 'bike', from: journey.pickupStation, to: journey.returnStation, expectedSeconds: journey.rideSeconds },
    { profile: 'foot', from: journey.returnStation, to: venue, expectedSeconds: journey.egressSeconds },
  ]
  const person = scheduledAlternatives(participant).find((candidate) =>
    candidate.arriveByJourneys?.some((arriveBy) => arriveBy === journey.journey)
    || candidate.result.journeys[journey.endpointIndex] === journey.journey)
  if (!person) return []
  const legs: PreviewLeg[] = []
  const access = journey.journey.legs.find((leg) => leg.kind === 'access')
  if (access?.kind === 'access' && access.mode !== 'bike-share') {
    const endpoint = person.endpoints[access.toStation]
    if (endpoint) legs.push({
      profile: access.mode === 'bike' ? 'bike' : 'foot',
      from: origin,
      to: { lat: endpoint[2], lon: endpoint[3] },
      expectedSeconds: access.travelSeconds ?? access.seconds,
    })
  }
  journey.journey.legs.forEach((leg) => {
    if (leg.kind !== 'transfer' || leg.generatedTransferIndex === undefined) return
    const from = person.endpoints[leg.fromStation]
    const to = person.endpoints[leg.toStation]
    if (from && to) legs.push({
      profile: 'foot', from: { lat: from[2], lon: from[3] }, to: { lat: to[2], lon: to[3] },
      expectedSeconds: leg.walkingSeconds ?? leg.seconds,
    })
  })
  if (!journey.bikeShareEgress) {
    const endpoint = person.endpoints[journey.endpointIndex]
    if (endpoint) legs.push({
      profile: 'foot', from: { lat: endpoint[2], lon: endpoint[3] }, to: venue,
      expectedSeconds: journey.egressSeconds,
    })
  }
  return legs
}

async function cachedRoadGeometry(leg: PreviewLeg, signal: AbortSignal): Promise<RoadGeometry> {
  const key = `${leg.profile}:${leg.from.lat},${leg.from.lon}>${leg.to.lat},${leg.to.lon}`
  const cached = previewCache.get(key)
  if (cached && durationMatches(cached.duration, leg.expectedSeconds)) return cached
  const geometry = await roadGeometry(leg.profile, leg.from, leg.to, signal)
  if (!durationMatches(geometry.duration, leg.expectedSeconds)) {
    throw new Error('Route geometry no longer matches the selected travel time.')
  }
  previewCache.set(key, geometry)
  return geometry
}

function durationMatches(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) <= Math.max(5, expected * 0.01)
}

function clearRoutePreview(): void {
  previewGeneration += 1
  previewController?.abort()
  previewController = null
  previewLayers.forEach((layer) => layer.remove())
  previewLayers = []
  retryRoutePreview = null
  refreshRouteRecovery = null
  requiredElement<HTMLElement>('#route-recovery').hidden = true
  requiredElement<HTMLElement>('#route-completion').textContent = ''
  refreshResultsNoticeFocus()
  document.querySelectorAll('.route-preview-status').forEach((status) => { status.textContent = '' })
}

function renderSearchAreas(
  areas: PlannedVenue[],
  radius: number,
  points: LocationPoint[],
  routedPeople: RoutedParticipant[],
  message: string,
  providerFailed = false,
): void {
  clearResultPresentation()
  resultsTitle.textContent = 'Search areas'
  renderSearchedDeparture()
  resultsError.className = providerFailed ? 'error' : 'empty-state'
  resultsError.textContent = message
  resultsError.hidden = false
  const explanation = document.createElement('p')
  explanation.className = 'area-explanation'
  explanation.textContent = 'These are candidate areas, not specific places. Select an area to search within its circle.'
  let selectedAreaIndex: number | null = null
  let expandedAreaIndex: number | null = null
  const markers: Marker[] = []
  const cards = areas.map((area, index) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'venue-card area-card'
    button.setAttribute('aria-pressed', 'false')
    const detailedRouteLines = area.journeys?.map((journey, personIndex) =>
      routeSummaryHtml(personLabel(personIndex), routedPeople[personIndex], journey)).join('') ?? ''
    const compactRouteLines = area.journeys?.map((journey, personIndex) =>
      routeSummaryHtml(personLabel(personIndex), routedPeople[personIndex], journey, 'compact')).join('') ?? ''
    const timeChips = area.minutes.map((minutes, personIndex) => `
      <span><b>${personLabel(personIndex)}</b> ${estimatedJourney(area.journeys?.[personIndex]) ? '~' : ''}${tripMinutes(area.journeys?.[personIndex], minutes)} min trip
        <small>${formatJourneyTime(area.journeys?.[personIndex], minutes * 60)}</small></span>
    `).join('')
    button.innerHTML = `
      <div class="venue-rank">${index + 1}</div>
      <div class="venue-copy">
        <div class="venue-heading"><h3>Area ${index + 1}</h3><span>Candidate</span></div>
        <div class="route-summary route-summary-detailed">${detailedRouteLines}</div>
        <div class="route-summary route-summary-compact">${compactRouteLines}</div>
        <div class="travel-times">${timeChips}</div>
      </div>
    `
    const circle = L.circle([area.lat, area.lon], {
      radius, color: routeColor(0), weight: 2, fillColor: routeColor(0), fillOpacity: 0.12,
    }).addTo(map)
    const marker = L.marker([area.lat, area.lon], { icon: venueIcon(index + 1), title: `Candidate area ${index + 1}` })
      .bindPopup(`<strong>Candidate search area ${index + 1}</strong>`, { autoPan: false })
      .addTo(map)
    const activateArea = (source: 'card' | 'marker'): void => {
      if (screen !== 'results' || searchOpen) return
      const selectionChanged = selectedAreaIndex !== index
      if (source === 'card') expandedAreaIndex = expandedAreaIndex === index ? null : index
      else if (selectionChanged) expandedAreaIndex = null
      selectedAreaIndex = index
      resultsError.hidden = true
      explanation.hidden = true
      if (areaRecovery) {
        areaRecovery.selectedIndex = index
        requiredElement<HTMLElement>('#area-search-context').textContent = `Area ${index + 1} selected`
        requiredElement<HTMLElement>('#area-search-actions').hidden = false
        requiredElement<HTMLButtonElement>('#search-area').disabled = clientIncompatible || !navigator.onLine || areaRecovery.pending
      }
      cards.forEach((card, cardIndex) => {
        const selected = cardIndex === index
        card.setAttribute('aria-pressed', String(selected))
        card.classList.toggle('venue-result-selected', selected)
        card.classList.toggle('area-expanded', cardIndex === expandedAreaIndex)
        markers[cardIndex].getElement()?.classList.toggle('venue-marker-selected', selected)
        markers[cardIndex].setZIndexOffset(selected ? 1_000 : 0)
      })
      if (compactResults.matches) {
        if (source === 'marker') resultsSheet.setState('partial')
        else if (expandedAreaIndex !== null) resultsSheet.setState('full')
      }
      if (selectionChanged) positionSelectedPlace([area.lat, area.lon], 14)
      if (compactResults.matches) map.closePopup()
      else marker.openPopup()
      if (source === 'marker') requestAnimationFrame(() => revealVenueCard(button))
    }
    button.addEventListener('click', () => activateArea('card'))
    marker.on('click', () => activateArea('marker'))
    marker.on('keypress', (event: LeafletKeyboardEvent) => {
      if (event.originalEvent.key === 'Enter') activateArea('marker')
    })
    markers.push(marker)
    venueLayers.push(circle, marker)
    return button
  })
  recommendationList.replaceChildren(explanation, ...cards)
  requiredElement<HTMLElement>('#places-count').textContent = `${areas.length} area${areas.length === 1 ? '' : 's'}`
  pendingMapBounds = [
    ...points.map((point) => [point.lat, point.lon] as [number, number]),
    ...areas.map((area) => [area.lat, area.lon] as [number, number]),
  ]
}

function areaSearchDiagnostic(
  reason: 'venue_unavailable',
  areas: PlannedVenue[],
  people: RoutedParticipant[],
  message?: string,
): SearchDiagnostic {
  return {
    state: 'partial',
    code: 'areas_only',
    reason,
    routing: routingDiagnostics(people),
    areas: areas.map(({ lat, lon, minutes, range }) => ({ lat, lon, minutes, range })),
    ...(message ? { message } : {}),
  }
}

function searchAreaStatus(count: number, prefix: string): string {
  return `${prefix} Showing ${count} candidate search area${count === 1 ? '' : 's'}.`
}

function requiresFinalTravelTimes(origin: OriginState): boolean {
  return origin.choice.kind === 'permissions' && origin.choice.modes.length > 0
}

function startSearchProgress(
  search: number,
  includeFinalTravelTimes: boolean,
  includeConnections: boolean,
): void {
  clearSearchProgress()
  const definitions: Array<Omit<SearchPhase, 'state'>> = [
    { id: 'trips', activeLabel: 'Comparing trips', completeLabel: 'Compared trips' },
    ...(includeConnections ? [{
      id: 'connections' as const,
      activeLabel: 'Confirming walking connections',
      completeLabel: 'Confirmed walking connections',
    }] : []),
    { id: 'areas', activeLabel: 'Finding shared areas', completeLabel: 'Found shared areas' },
    { id: 'venues', activeLabel: 'Finding nearby places', completeLabel: 'Found nearby places' },
    ...(includeFinalTravelTimes ? [{
      id: 'travel-times' as const,
      activeLabel: 'Checking final travel times',
      completeLabel: 'Checked final travel times',
    }] : []),
    { id: 'ranking', activeLabel: 'Ranking recommendations', completeLabel: 'Ranked recommendations' },
  ]
  searchProgress = {
    search,
    phases: definitions.map((phase) => ({ ...phase, state: 'pending' })),
  }
  status.classList.add('status-searching')
  advanceSearchPhase('trips')
}

function advanceSearchPhase(id: SearchPhaseId): void {
  if (!searchProgress) return
  completeActiveSearchPhase()
  const phase = searchProgress.phases.find((candidate) => candidate.id === id)
  if (!phase) return
  phase.state = 'active'
  phase.startedAt = performance.now()
  status.textContent = `${phase.activeLabel}…`
  renderSearchProgress()
}

function completeActiveSearchPhase(): void {
  const active = searchProgress?.phases.find((phase) => phase.state === 'active')
  if (!active) return
  active.state = 'complete'
  active.durationMs = Math.max(0, Math.round(performance.now() - (active.startedAt ?? performance.now())))
}

function finishSearchProgress(): void {
  completeActiveSearchPhase()
  saveSearchPhaseTimings()
  clearSearchProgress(false)
}

function failSearchProgress(): void {
  const active = searchProgress?.phases.find((phase) => phase.state === 'active')
  if (active) {
    active.state = 'failed'
    active.durationMs = Math.max(0, Math.round(performance.now() - (active.startedAt ?? performance.now())))
  }
  if (searchProgress) searchProgress.phases = searchProgress.phases.filter((phase) => phase.state !== 'pending')
  saveSearchPhaseTimings()
  renderSearchProgress()
}

function renderSearchProgress(): void {
  if (!searchProgress) return
  const list = document.createElement('ol')
  list.className = 'search-progress'
  list.setAttribute('aria-label', 'Search progress')
  for (const phase of searchProgress.phases) {
    const item = document.createElement('li')
    item.className = `search-phase search-phase-${phase.state}`
    if (phase.state === 'active') item.setAttribute('aria-current', 'step')
    const marker = document.createElement('span')
    marker.className = 'search-phase-marker'
    marker.setAttribute('aria-hidden', 'true')
    marker.textContent = phase.state === 'complete' ? '✓' : phase.state === 'failed' ? '!' : phase.state === 'active' ? '•' : '○'
    const label = document.createElement('span')
    label.textContent = phase.state === 'complete'
      ? phase.completeLabel
      : phase.state === 'failed'
        ? `${phase.activeLabel} failed`
        : `${phase.activeLabel}${phase.state === 'active' ? '…' : ''}`
    item.appendChild(marker)
    item.appendChild(label)
    list.appendChild(item)
  }
  // Keep the disclosure and its focus stable as provider phases change.
  let details = searchProgressRegion.querySelector('details')
  if (!details) {
    const current = document.createElement('p')
    current.className = 'search-current-phase'
    details = document.createElement('details')
    const summary = document.createElement('summary')
    details.appendChild(summary)
    searchProgressRegion.replaceChildren(current, details)
  }
  const current = searchProgressRegion.querySelector<HTMLElement>('.search-current-phase')!
  const phase = searchProgress.phases.find((candidate) => candidate.state === 'active')
    ?? searchProgress.phases.find((candidate) => candidate.state === 'failed')
  current.textContent = phase ? `${phase.activeLabel}${phase.state === 'failed' ? ' failed' : '…'}` : ''
  current.classList.toggle('search-phase-failed', phase?.state === 'failed')
  const completed = searchProgress.phases.filter((candidate) => candidate.state === 'complete').length
  details.querySelector('summary')!.textContent = completed
    ? `${completed} ${completed === 1 ? 'step' : 'steps'} complete` : 'Search steps'
  details.querySelector('ol')?.remove()
  details.appendChild(list)
}

function saveSearchPhaseTimings(): void {
  if (!searchProgress) return
  lastSearchPhases = searchProgress.phases
    .filter((phase) => phase.state !== 'pending')
    .map(({ id, state, durationMs }) => ({ id, state, durationMs }))
}

function searchProgressPhases(): Array<Pick<SearchPhase, 'id' | 'state' | 'durationMs'>> {
  if (!searchProgress) return lastSearchPhases
  return searchProgress.phases
    .filter((phase) => phase.state !== 'pending')
    .map(({ id, state, durationMs, startedAt }) => ({
      id,
      state,
      durationMs: state === 'active'
        ? Math.max(0, Math.round(performance.now() - (startedAt ?? performance.now())))
        : durationMs,
    }))
}

function clearSearchProgress(clearTimings = true): void {
  searchProgress = null
  searchProgressRegion.replaceChildren()
  if (clearTimings) lastSearchPhases = []
}

async function readyBus(): Promise<BusClient> {
  bus ??= new BusClient()
  await bus.ready
  return bus
}

async function readyFerry(): Promise<FerryClient> {
  ferry ??= new FerryClient()
  await ferry.ready
  return ferry
}

async function readyMultimodal(): Promise<MultimodalClient> {
  multimodal ??= new MultimodalClient()
  await multimodal.ready
  return multimodal
}

function configureDepartureControls(): void {
  const backends = selectedBackends()
  const scheduledBackends = backends.filter(isScheduledBackend)
  const defaultDate = selectedTimeIntent() === 'arrive-by'
    ? defaultArrivalTime().date : newYorkDateTime().date
  const selectedDate = departureEdited && !leaveNow ? departureDate.value : defaultDate
  departureTime.disabled = false
  if (!scheduledBackends.length) {
    setDepartureDateOptions(upcomingServiceDates(), selectedDate)
    return
  }
  const readyBackends = scheduledBackends.filter(backendReady)
  if (!readyBackends.length) {
    setDepartureDateOptions(upcomingServiceDates(), selectedDate)
    return
  }
  const dates = availableDates()
  setDepartureDateOptions(dates, selectedDate)
}

function setDepartureDateOptions(dates: string[], selected: string): void {
  const options = dates.map((date) => {
    const option = document.createElement('option')
    option.value = date
    option.textContent = formatServiceDate(date)
    return option
  })
  if (departureEdited && /^\d{8}$/.test(selected) && !dates.includes(selected)) {
    const unavailable = document.createElement('option')
    unavailable.value = selected
    unavailable.textContent = `${formatServiceDate(selected)} (unavailable)`
    unavailable.disabled = true
    unavailable.dataset.unavailable = 'true'
    options.unshift(unavailable)
  }
  departureDate.replaceChildren(...options)
  if (options.some((option) => option.value === selected)) departureDate.value = selected
  departureDate.disabled = !dates.length
}

function refreshModeControls(): void {
  configureDepartureControls()
  const backends = selectedBackends()
  const scheduledBackends = backends.filter(isScheduledBackend)
  const choicesValid = origins.every((origin) => origin.choice.kind !== 'none')
  const readyBackends = scheduledBackends.filter(backendReady)
  const hasCommonDates = !readyBackends.length || availableDates().length > 0
  const dateAvailable = departureDate.selectedOptions[0]?.dataset.unavailable !== 'true'
  const canSearch = choicesValid && hasCommonDates && dateAvailable && origins.every((origin) => {
    if (origin.choice.kind !== 'permissions') return false
    const wholeJourney = origin.choice.modes.some((mode) => mode === 'walk' || mode === 'taxi'
      || mode === 'bike' || mode === 'bike-share')
    const backend = routingBackend(origin.choice)
    return wholeJourney || isScheduledBackend(backend) && backendReady(backend)
  })
  const searching = searchDiagnostic?.state === 'searching'
  findButton.textContent = searching ? 'Finding places…' : 'Find places'
  findButton.disabled = clientIncompatible || !navigator.onLine || searching || !allOriginsSelected() || !canSearch
  findButton.setAttribute('aria-describedby', navigator.onLine ? 'find-help' : 'offline-notice')
  const help = requiredElement<HTMLElement>('#find-help')
  help.textContent = clientIncompatible || !navigator.onLine || searching ? ''
    : !allOriginsSelected() ? 'Choose a starting point for each person.'
      : !choicesValid ? 'Choose at least one travel option for each person.'
        : !hasCommonDates ? 'Selected schedules have no date in common. Change travel options.'
          : !dateAvailable ? 'Choose an available date.'
            : !canSearch ? scheduledBackends.some(backendUnavailableReason)
              ? 'Selected schedules are unavailable. Choose another travel option.'
              : 'Loading selected schedules…'
              : ''
  help.hidden = !help.textContent
  requiredElement<HTMLButtonElement>('#retry-search').disabled = clientIncompatible || !navigator.onLine || searching
  requiredElement<HTMLButtonElement>('#search-area').disabled = clientIncompatible || !navigator.onLine || searching || !areaRecovery
  if (clientIncompatible) {
    originsContainer.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach((button) => {
      button.disabled = true
    })
  }
  if (!hasResults) {
    routingMode.hidden = false
    if (!choicesValid) {
      routingMode.textContent = 'Choose travel options'
    } else if (canSearch) {
      routingMode.textContent = ''
    } else if (readyBackends.length && !hasCommonDates) {
      routingMode.textContent = 'No common schedule date'
    } else {
      const missing = scheduledBackends.filter((backend) => !backendReady(backend))
      const unavailable = missing.filter((backend) => backendUnavailableReason(backend))
      const loading = missing.filter((mode) => !unavailable.includes(mode))
      const messages = [
        ...(unavailable.length ? [`${capitalizeModes(unavailable)} schedules unavailable`] : []),
        ...(loading.length ? [`loading ${loading.join(' and ')} times`] : []),
      ]
      routingMode.textContent = messages.join('; ')
    }
  }
  if (!hasResults) {
    const unavailableBackends = scheduledBackends.filter((backend) => backendUnavailableReason(backend))
    const loadingBackends = scheduledBackends.filter((backend) =>
      !backendReady(backend) && !backendUnavailableReason(backend))
    const availabilityNote = [
      ...(unavailableBackends.length
        ? [`${capitalizeModes(unavailableBackends)} schedules unavailable; those routes will be skipped.`] : []),
      ...(loadingBackends.length ? [`Loading ${loadingBackends.join(' and ')} schedules.`] : []),
    ].join(' ')
    scheduleNote.textContent = choicesValid && canSearch
      ? `${scheduleDescription()}${availabilityNote ? ` ${availabilityNote}` : ''}`
      : choicesValid && readyBackends.length && !hasCommonDates
          ? 'The selected schedules have no date in common.'
          : choicesValid ? 'Waiting for the selected schedules.' : 'Choose a travel option for each person.'
  }
}

function availableDates(): string[] {
  const today = todayInNewYork()
  const sets = selectedBackends().filter(isScheduledBackend).filter(backendReady).map(backendDates)
  return sets[0]?.filter((date) => date >= today
    && sets.slice(1).every((dates) => dates.includes(date))) ?? []
}

function todayInNewYork(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts()
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ''
  return `${part('year')}${part('month')}${part('day')}`
}

function selectedBackends(): RoutingBackend[] {
  return [...new Set(origins.map((origin) => routingBackend(origin.choice)))]
    .filter((backend) => backend !== 'none')
}

function isScheduledBackend(backend: RoutingBackend): backend is ScheduledMode | 'multimodal' {
  return backend === 'subway' || backend === 'bus' || backend === 'ferry' || backend === 'multimodal'
}

function backendReady(backend: ScheduledMode | 'multimodal'): boolean {
  if (backend === 'subway') return Boolean(transit.metadata)
  if (backend === 'bus') return Boolean(bus?.metadata)
  if (backend === 'ferry') return Boolean(ferry?.metadata)
  return Boolean(multimodal?.metadata)
}

function backendDates(backend: ScheduledMode | 'multimodal'): string[] {
  if (backend === 'subway') return transit.metadata?.dates ?? []
  if (backend === 'bus') return bus?.metadata?.dates ?? []
  if (backend === 'ferry') return ferry?.metadata?.dates ?? []
  return multimodal?.metadata?.dates ?? []
}

function backendUnavailableReason(backend: ScheduledMode | 'multimodal'): string | null | undefined {
  if (backend === 'subway') return transit.unavailableReason
  if (backend === 'bus') return bus?.unavailableReason
  if (backend === 'ferry') return ferry?.unavailableReason
  return multimodal?.unavailableReason
}

function capitalizeModes(modes: string[]): string {
  const value = modes.join(' and ')
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function clearOrigin(state: OriginState, form: HTMLFormElement, input: HTMLInputElement): void {
  state.generation += 1
  const wasLocating = locationRequest?.origin === state
  cancelLocationRequest(state)
  delete state.selectionSource
  setLocationFeedback(form, '')
  input.hidden = false
  requiredChild<HTMLElement>(form, '.selected-location-row').hidden = true
  requiredChild<HTMLButtonElement>(form, 'button[type="submit"]').hidden = false
  requiredChild<HTMLDivElement>(form, '.search-results').replaceChildren()
  form.querySelector('.set-saved-place')?.remove()
  if (!state.point) {
    if (wasLocating) refreshSavedPlaceControls()
    return
  }
  state.point = null
  input.title = ''
  state.layer?.remove()
  delete state.layer
  refreshSavedPlaceControls()
  invalidateSearch()
}

function refreshOriginMarkers(): void {
  origins.forEach((state, index) => {
    state.layer?.remove()
    if (state.point) {
      state.layer = L.marker([state.point.lat, state.point.lon], { icon: originIcon(personLabel(index)) }).addTo(map)
    }
  })
}

function invalidateSearch(): void {
  const hadResults = hasResults
  searchController?.abort(new Error('Superseded by changed search inputs.'))
  searchController = null
  activeSearch += 1
  completedSearch = null
  updateSearchToggle()
  areaRecovery = null
  fixtureRoutePreviews = null
  clearResultPresentation()
  hasResults = false
  searchDiagnostic = null
  lastBikeShareUpdatedAt = null
  searchedDeparture = null
  clearSearchProgress()
  status.classList.remove('status-searching')
  resultsTitle.textContent = 'Places'
  searchedTime.hidden = true
  recommendationList.innerHTML = hadResults ? '<p class="empty-state">Search again.</p>' : ''
  refreshModeControls()
  status.textContent = ''
}

function selectedOrigins(): LocationPoint[] {
  return origins.flatMap(({ point }) => point ? [point] : [])
}

function allOriginsSelected(): boolean {
  return origins.length >= minimumOrigins && origins.every(({ point }) => point !== null)
}

function canonicalPermissions(modes: TravelMode[]): TravelPermissionSet {
  const selected = new Set(modes)
  const order: TravelMode[] = ['walk', 'subway', 'bus', 'ferry', 'taxi', 'bike', 'bike-share']
  return order.filter((mode) => selected.has(mode)) as TravelPermissionSet
}

function updateModeError(form: HTMLFormElement, state: OriginState): void {
  requiredChild<HTMLParagraphElement>(form, '.mode-error').hidden = state.choice.kind !== 'none'
  requiredChild<HTMLFieldSetElement>(form, '.travel-mode').setAttribute('aria-describedby',
    `mode-help-${state.id} mode-connections-${state.id}${state.choice.kind === 'none' ? ` mode-error-${state.id}` : ''}`)
}

function personLabel(index: number): string {
  return String.fromCharCode(65 + index)
}

function personName(index: number): string {
  if (index === 0) return 'First person'
  if (index === 1) return 'Second person'
  return `Person ${personLabel(index)}`
}

function modeOptionHtml(
  type: 'checkbox' | 'radio',
  name: string,
  value: TravelMode,
  label: string,
  checked: boolean,
): string {
  return `
    <label class="mode-option">
      <input type="${type}" name="${name}" value="${value}" ${checked ? 'checked' : ''} />
      ${modeIcon(value)}
      <span>${label}</span>
      <span class="mode-option-check" aria-hidden="true">✓</span>
    </label>
  `
}

function formatServiceDate(value: string): string {
  const date = new Date(`${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T12:00:00`)
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date)
}

function scheduleDescription(): string {
  const choices = origins.flatMap((origin) =>
    origin.choice.kind === 'permissions' ? [origin.choice] : [])
  const permissions = choices.flatMap((choice) => choice.modes)
  const roadModes = permissions.filter((mode): mode is RoadMode => mode === 'taxi' || mode === 'bike')
  const usesBikeShare = permissions.includes('bike-share')
  const usesWalking = permissions.includes('walk')
  const usesBicycleAccess = choices.some((choice) => choice.modes.includes('bike')
    && scheduledModesFor(choice).length > 0)
  const usesBikeShareConnection = choices.some((choice) => choice.modes.includes('bike-share')
    && scheduledModesFor(choice).length > 0)
  const bikeShareNote = usesBikeShare
    ? `${lastBikeShareUpdatedAt
      ? `Availability reported as of ${formatAvailabilityTime(lastBikeShareUpdatedAt)} and may change.`
      : 'Station availability may change.'} Bikes and docks are not reserved; verify before departure.`
    : ''
  const scheduledModes = permissions.filter((mode): mode is ScheduledMode =>
    mode === 'subway' || mode === 'bus' || mode === 'ferry')
  const notes: string[] = []
  if (scheduledModes.length) {
    const lastDate = availableDates().at(-1)
    const services = [...new Set(scheduledModes)].map((mode) => modeLabel(mode).toLowerCase())
      .join(', ').replace(/, ([^,]+)$/, ' and $1')
    const authority = scheduledModes.includes('ferry') ? 'Scheduled' : 'Scheduled MTA'
    notes.push(`${authority} ${services} service${lastDate
      ? ` through ${formatServiceDate(lastDate)}` : ''} plus necessary walks; not real-time.`)
  }
  if (usesWalking) notes.push('Walking times follow pedestrian routes.')
  if (roadModes.includes('taxi')) {
    notes.push('Taxi estimates are static and include a 5-minute pickup allowance; no live traffic, dispatch, fares, or availability.')
  }
  if (roadModes.includes('bike')) notes.push('Bicycle-route estimates do not include live conditions.')
  if (usesBicycleAccess) notes.push('Bicycle-to-transit access includes a 3-minute parking allowance.')
  if (usesBikeShareConnection) notes.push('Bike-share connections include a 2-minute unlock and return allowance.')
  if (bikeShareNote) notes.push(bikeShareNote)
  if (notes.length === 1 && usesWalking && !scheduledModes.length) return 'Walking times follow pedestrian routes.'
  return notes.join(' ')
}

function routingDescription(people: RoutedParticipant[]): string {
  const permissionSets = people.map((person) => person.permissions.join(','))
  if (permissionSets.every((permissions) => permissions === permissionSets[0])
    && people[0].permissions.length === 1) {
    const mode = people[0].permissions[0]
    return mode === 'taxi' ? 'Taxi / ride-share estimates'
      : mode === 'bike-share' ? 'Bike-share estimates'
        : mode === 'bike' ? 'Bicycle estimates'
          : mode === 'walk' ? 'Walking times' : `${modeLabel(mode)} times`
  }
  return 'Best permitted travel times'
}

function formatAvailabilityTime(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
  }).format(new Date(timestamp * 1_000))
}

function departureChanged(): void {
  departureEdited = true
  invalidateSearch()
}

function selectedTimeIntent(): TimeIntent {
  return timeIntentInputs.find((input) => input.checked)?.value === 'arrive-by' ? 'arrive-by' : 'depart-at'
}

function setTimeIntent(intent: TimeIntent | 'leave-now'): void {
  leaveNow = intent === 'leave-now'
  timeIntentInputs.forEach((input) => { input.checked = input.value === intent })
  updateTimeControlLabels()
}

function updateTimeControlLabels(): void {
  document.querySelectorAll<HTMLElement>('.scheduled-time').forEach((field) => { field.hidden = leaveNow })
  const arrival = selectedTimeIntent() === 'arrive-by'
  departureDate.setAttribute('aria-label', arrival ? 'Arrival date' : 'Departure date')
  departureTime.setAttribute('aria-label', arrival ? 'Arrival time' : 'Departure time')
}

function defaultArrivalTime(): PlannerDateTime {
  return newYorkDateTime(new Date(Date.now() + 60 * 60 * 1_000), 15)
}

function setDepartureSelection(selection: PlannerDateTime): void {
  if (![...departureDate.options].some((option) => option.value === selection.date)) {
    const dates = upcomingServiceDates()
    if (dates.includes(selection.date)) setDepartureDateOptions(dates, selection.date)
  }
  departureDate.value = selection.date
  departureTime.value = selection.time
}

function captureDepartureForSearch(): SearchTime {
  if (leaveNow) setDepartureSelection(newYorkDateTime())
  else if (!departureEdited) setDepartureSelection(
    selectedTimeIntent() === 'arrive-by' ? defaultArrivalTime() : newYorkDateTime(new Date(), 15),
  )
  if (!/^\d{8}$/.test(departureDate.value) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(departureTime.value)) {
    throw new Error('Choose a valid date and time.')
  }
  const intent = selectedTimeIntent()
  const selection = { date: departureDate.value, time: departureTime.value, intent }
  const now = newYorkDateTime()
  if (intent === 'arrive-by' && `${selection.date}${selection.time}` <= `${now.date}${now.time}`) {
    throw new Error('Choose an arrival time in the future.')
  }
  return selection
}

function renderSearchedDeparture(): void {
  if (!searchedDeparture) return
  searchedTime.textContent = `${searchedDeparture.intent === 'arrive-by' ? 'Arriving by' : 'Leaving'} ${
    formatServiceDate(searchedDeparture.date)} at ${formatClock(searchedDeparture.time)}.`
  searchedTime.hidden = false
}

function tripMinutes(journey: EndpointJourney | undefined, fallbackMinutes: number): number {
  return journey ? Math.ceil(journey.durationSeconds / 60) : fallbackMinutes
}

function formatJourneyTime(journey: EndpointJourney | undefined, fallbackSeconds: number): string {
  if (!searchedDeparture) return ''
  if (searchedDeparture.intent === 'arrive-by' && journey?.departureSeconds !== undefined) {
    const arrival = journey.venueArrivalSeconds ?? journey.departureSeconds + journey.durationSeconds
    return `Leave ${formatServiceSeconds(journey.departureSeconds)} · Arrive ${formatServiceSeconds(arrival)}`
  }
  return `Arrives ${formatServiceSeconds(timeSeconds(searchedDeparture.time)
    + Math.ceil((journey?.durationSeconds ?? fallbackSeconds) / 60) * 60)}`
}

function formatServiceSeconds(seconds: number): string {
  if (!searchedDeparture) return ''
  const totalMinutes = Math.floor(seconds / 60)
  const dayOffset = Math.floor(totalMinutes / 1_440)
  const dayMinutes = ((totalMinutes % 1_440) + 1_440) % 1_440
  const time = `${Math.floor(dayMinutes / 60).toString().padStart(2, '0')}`
    + `:${(dayMinutes % 60).toString().padStart(2, '0')}`
  const date = dayOffset ? `${formatServiceDate(addServiceDays(searchedDeparture.date, dayOffset))} ` : ''
  return `${date}${formatClock(time)}`
}

function formatClock(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`
}

function originIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: 'map-marker-shell', html: `<span class="origin-marker">${label}</span>`,
    iconSize: [38, 38], iconAnchor: [19, 19],
  })
}

function venueIcon(rank: number): L.DivIcon {
  return L.divIcon({
    className: 'map-marker-shell', html: `<span class="venue-marker">${rank}</span>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
  })
}

function requiredElement<T>(selector: string): T {
  const element = document.querySelector(selector)
  if (!element) throw new Error(`Missing element: ${selector}`)
  return element as T
}

function requiredChild<T>(parent: { querySelector(selector: string): Element | null }, selector: string): T {
  const element = parent.querySelector(selector)
  if (!element) throw new Error(`Missing child element: ${selector}`)
  return element as T
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : 'Something went wrong. Try again.'
}

function escapeHtml(value: string): string {
  const element = document.createElement('div')
  element.textContent = value
  return element.innerHTML
}

function estimatedJourney(journey: EndpointJourney | undefined): boolean {
  if (!journey) return true
  if (journey.kind === 'walk') return journey.source !== 'routed'
  return !('journey' in journey)
}
